import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import useSWR from "swr";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, Spinner, Empty, StatusBadge, KV, HelpIcon } from "@/components/Atoms";
import { Server, Network, Database, HardDrive, Activity, FileText, Zap, Shield, Plus, Trash2, RefreshCw, FlaskConical, Settings as SettingsIcon, Mic, Boxes, KeyRound, Copy, Mail, List, Puzzle, Bell, Stethoscope, Terminal, Wrench, Power, Pause, Sparkles } from "lucide-react";
import AppApiVmSizesPanel from "@/components/AppApiVmSizesPanel";
import LocalAiPanel from "@/components/LocalAiPanel";
import OccPolicyPanel from "@/components/OccPolicyPanel";
import FleetRepairPanel from "@/components/FleetRepairPanel";
import DiagnosticsPage from "@/pages/DiagnosticsPage";
import { MonitoringTab, HealthBadge, HEALTH_META } from "@/components/ServiceHealth";
import DbProxyPanel from "@/components/DbProxyPanel";
import {
    RedisPoolPanel,
    CollaboraPoolPanel,
    EuroOfficePoolPanel,
    CaddyLbPanel,
    AppStoreProxyPanel,
    NotifyPushPanel,
    AntivirusPanel,
    BimiConnectorPanel,
} from "@/pages/InfrastructurePoolsExtra";
import {
    TalkHpbPanel,
} from "@/pages/InfrastructureTalkHpb";

const fetcher = (u) => api.get(u).then((r) => r.data);

// PMG / Mail settings live in their own tab (PmgMailPanel) — keep them out of
// the generic Settings panel so they aren't shown twice.
const PMG_KEYS = new Set([
    "pmg_api_url", "pmg_api_user", "pmg_api_password",
    "pmg_relay_hosts", "pmg_relay_port",
]);

const TABS = [
    { key: "health", label: "Health", icon: Activity },
    { key: "proxmox", label: "Proxmox", icon: Server },
    { key: "ip-pools", label: "IP Pools", icon: Network },
    { key: "ip-map", label: "IP Map", icon: List },
    { key: "sdn", label: "SDN Zones", icon: Network },
    { key: "db", label: "DB Pools", icon: Database },
    { key: "db-proxy", label: "DB-Proxy", icon: Database },
    { key: "s3", label: "Object Storage", icon: HardDrive },
    { key: "redis", label: "Redis Pools", icon: Activity },
    { key: "collabora", label: "Collabora", icon: FileText },
    { key: "eurooffice", label: "Euro-Office", icon: FileText },
    { key: "lb", label: "Loadbalancers", icon: Shield },
    { key: "appstore", label: "AppStore Proxy", icon: Activity },
    { key: "notify-push", label: "Notify Push", icon: Zap },
    { key: "talk-hpb", label: "Talk HPB", icon: Mic },
    { key: "appapi", label: "AppAPI", icon: Puzzle },
    { key: "local-ai", label: "Lokal-AI", icon: Sparkles },
    { key: "antivirus", label: "Antivirus", icon: Shield },
    { key: "bimi", label: "BIMI", icon: Mail },
    { key: "archive", label: "Mail Archive", icon: HardDrive },
    { key: "nas", label: "Shared Storage (NAS)", icon: Boxes },
    { key: "pmg", label: "PMG / Mail", icon: Mail },
    { key: "diagnostics", label: "Diagnostics", icon: Stethoscope },
    { key: "fleet-repair", label: "Fleet Repair", icon: Wrench },
    { key: "occ-policy", label: "OCC Policy", icon: Terminal },
    { key: "settings", label: "Settings", icon: SettingsIcon },
];

const TAB_GROUPS = [
    { label: "Monitor", keys: ["health", "diagnostics", "fleet-repair"] },
    { label: "Compute & Network", keys: ["proxmox", "ip-pools", "ip-map", "sdn", "nas"] },
    { label: "Data Stores", keys: ["db", "db-proxy", "s3", "redis", "archive"] },
    { label: "Services", keys: ["collabora", "lb", "appstore", "notify-push", "talk-hpb", "appapi", "local-ai", "antivirus", "bimi", "pmg"] },
    { label: "Policy", keys: ["occ-policy", "settings"] },
];

const TAB_BY_KEY = Object.fromEntries(TABS.map((t) => [t.key, t]));

export default function InfrastructurePage() {
    const [tab, setTab] = useState("health");
    const activeGroup = TAB_GROUPS.find((g) => g.keys.includes(tab)) || TAB_GROUPS[0];
    const selectGroup = (g) => {
        if (g.keys.includes(tab)) return;
        setTab(g.keys[0]);
    };
    return (
        <div data-testid="infra-page">
            <PageHeader
                testid="infra-header"
                title="Infrastructure"
                subtitle="Self-managed Proxmox clusters, SDN zones and resource pools."
                action={
                    <Link to="/tokens" data-testid="infra-api-tokens-link"
                        className="cm-btn-secondary cm-btn-sm flex items-center gap-2">
                        <KeyRound size={14} /> API Tokens
                    </Link>
                }
            />

            {/* Category row */}
            <div className="px-4 sm:px-6 pt-4 flex flex-wrap items-center gap-2 border-b border-cm-line pb-0">
                {TAB_GROUPS.map((g) => {
                    const active = g === activeGroup;
                    return (
                        <button
                            key={g.label}
                            onClick={() => selectGroup(g)}
                            aria-selected={active}
                            role="tab"
                            className={`px-3 py-2 text-xs font-mono uppercase tracking-widest border-b-2 transition-colors ${
                                active
                                    ? "border-cm-brand text-cm-brand"
                                    : "border-transparent text-cm-muted hover:text-cm-text"
                            }`}
                        >
                            {g.label}
                        </button>
                    );
                })}
            </div>

            {/* Tabs of the active category */}
            <div className="px-4 sm:px-6 pt-3 flex flex-wrap items-center gap-1.5 border-b border-cm-line pb-3">
                {activeGroup.keys.map((key) => {
                    const t = TAB_BY_KEY[key];
                    if (!t) return null;
                    const Icon = t.icon;
                    const active = tab === t.key;
                    return (
                        <button
                            key={t.key}
                            data-testid={`infra-tab-${t.key}`}
                            onClick={() => setTab(t.key)}
                            role="tab"
                            aria-selected={active}
                            className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider border flex items-center gap-1.5 transition-colors ${
                                active
                                    ? "bg-cm-brand text-cm-bg border-cm-brand font-bold"
                                    : "bg-cm-bg text-cm-muted border-cm-line2 hover:text-cm-text hover:border-cm-line"
                            }`}
                        >
                            <Icon size={12} />
                            {t.label}
                        </button>
                    );
                })}
            </div>

            <div className="p-4 sm:p-6 pt-4">
                {tab === "health" && <MonitoringTab />}
                {tab === "proxmox" && <ProxmoxPanel />}
                {tab === "ip-pools" && <IpPoolsPanel />}
                {tab === "ip-map" && <IpMapPanel />}
                {tab === "sdn" && <SdnPanel />}
                {tab === "db" && <DbPoolPanel />}
                {tab === "db-proxy" && <DbProxyPanel />}
                {tab === "s3" && <S3PoolPanel />}
                {tab === "redis" && <RedisPoolPanel />}
                {tab === "collabora" && <CollaboraPoolPanel />}
                {tab === "eurooffice" && <EuroOfficePoolPanel />}
                {tab === "lb" && <CaddyLbPanel />}
                {tab === "appstore" && <AppStoreProxyPanel />}
                {tab === "notify-push" && <NotifyPushPanel />}
                {tab === "bimi" && <BimiConnectorPanel />}
                {tab === "archive" && <ArchiveInfraPanel />}
                {tab === "talk-hpb" && <TalkHpbPanel />}
                {tab === "appapi" && <AppApiVmSizesPanel />}
                {tab === "local-ai" && <LocalAiPanel />}
                {tab === "antivirus" && <AntivirusPanel />}
                {tab === "nas" && <NasPanel />}
                {tab === "pmg" && <PmgMailPanel />}
                {tab === "diagnostics" && <DiagnosticsPage />}
                {tab === "fleet-repair" && <FleetRepairPanel />}
                {tab === "occ-policy" && <OccPolicyPanel />}
                {tab === "settings" && <SettingsPanel />}
            </div>
        </div>
    );
}

// =========================================================
// IP Map — consolidated view of every allocated IP so the
// operator can set / verify firewall blocks manually.
// =========================================================
function IpMapPanel() {
    const { data, error, mutate } = useSWR(
        "/api/admin/infra/ip-map", fetcher, { refreshInterval: 30000 });
    const [zone, setZone] = useState("all");
    const [q, setQ] = useState("");
    if (error) return <Empty title="IP-Map konnte nicht geladen werden" />;
    if (!data) return <Spinner />;
    const rows = data.ips || [];
    const zones = ["all", ...Object.keys(data.summary || {})];
    const filtered = rows.filter((r) => {
        if (zone !== "all" && r.zone !== zone) return false;
        if (q) {
            const s = q.toLowerCase();
            return [r.ip, r.kind, r.name, r.node, String(r.vmid || "")]
                .some((v) => (v || "").toLowerCase().includes(s));
        }
        return true;
    });
    const zoneColor = (z) => ({
        public: "bg-amber-500/15 text-amber-300 border-amber-700/40",
        lb: "bg-violet-500/15 text-violet-300 border-violet-700/40",
        service: "bg-sky-500/15 text-sky-300 border-sky-700/40",
        tenant: "bg-emerald-500/15 text-emerald-300 border-emerald-700/40",
    }[z] || "bg-zinc-700/30 text-zinc-300 border-zinc-700");
    return (
        <div data-testid="ip-map-panel" className="space-y-4">
            <div className="cm-card p-3 text-xs text-zinc-400 bg-zinc-950 border border-zinc-800 leading-relaxed">
                Konsolidierte Liste aller allokierten IPs (Service-VMs, Pool-Gateways, Tenant-VMs).
                <span className="text-amber-300">Public IPs</span> need firewall attention —
                Port-Hinweise stehen in der letzten Spalte. Aktualisiert sich alle 30s.
            </div>
            <div className="flex flex-wrap items-center gap-2">
                {zones.map((z) => (
                    <button
                        key={z}
                        data-testid={`ipmap-zone-${z}`}
                        onClick={() => setZone(z)}
                        className={`text-xs uppercase font-mono px-2.5 py-1 border rounded-sm ${
                            zone === z
                                ? "bg-[#CCFF00] text-[#05070A] border-[#CCFF00]"
                                : "bg-zinc-950 text-zinc-400 border-zinc-800 hover:text-zinc-100"
                        }`}
                    >
                        {z}{z === "all" ? ` (${data.total})` : ` (${data.summary[z]})`}
                    </button>
                ))}
                <div className="flex-1" />
                <input
                    data-testid="ipmap-search"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Suche IP / Name / Kind…"
                    className="cm-input text-xs font-mono w-56"
                />
                <button
                    data-testid="ipmap-refresh"
                    onClick={() => mutate()}
                    className="cm-btn-ghost text-xs px-3 py-2 flex items-center gap-1"
                >
                    <RefreshCw size={12} /> Refresh
                </button>
            </div>
            <div className="cm-card overflow-x-auto p-0 border border-zinc-800">
                <table className="w-full text-xs font-mono" data-testid="ip-map-table">
                    <thead>
                        <tr className="text-zinc-500 uppercase text-[11px] tracking-wider border-b border-zinc-800">
                            <th className="text-left px-3 py-2">IP</th>
                            <th className="text-left px-3 py-2">Zone</th>
                            <th className="text-left px-3 py-2">Kind</th>
                            <th className="text-left px-3 py-2">Name</th>
                            <th className="text-left px-3 py-2">VMID</th>
                            <th className="text-left px-3 py-2">Status</th>
                            <th className="text-left px-3 py-2">Open Ports</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map((r, i) => (
                            <tr
                                key={`${r.ip}-${r.nic}-${i}`}
                                data-testid={`ipmap-row-${r.ip}`}
                                className="border-b border-zinc-900 hover:bg-zinc-900/40"
                            >
                                <td className="px-3 py-2 text-zinc-100">
                                    {r.ip}
                                    {r.nic && r.nic !== "primary" && (
                                        <span className="ml-1 text-[10px] text-zinc-500">({r.nic})</span>
                                    )}
                                </td>
                                <td className="px-3 py-2">
                                    <span className={`px-1.5 py-0.5 border rounded-sm text-[11px] uppercase ${zoneColor(r.zone)}`}>
                                        {r.zone}
                                    </span>
                                </td>
                                <td className="px-3 py-2 text-zinc-300">{r.kind}</td>
                                <td className="px-3 py-2 text-zinc-400 max-w-[16rem] truncate">{r.name}</td>
                                <td className="px-3 py-2 text-zinc-500">{r.vmid ?? "—"}</td>
                                <td className="px-3 py-2 text-zinc-400">{r.status}</td>
                                <td className="px-3 py-2 text-zinc-500">{r.ports || "—"}</td>
                            </tr>
                        ))}
                        {filtered.length === 0 && (
                            <tr>
                                <td colSpan={7} className="px-3 py-8 text-center text-zinc-600">
                                    No IPs
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}


// =========================================================
// Settings panel — operator-tunable platform-wide settings
// =========================================================
function AlertTestCard() {
    const [busy, setBusy] = useState(false);
    const [res, setRes] = useState(null);
    const [err, setErr] = useState(null);
    const send = async () => {
        setBusy(true); setErr(null); setRes(null);
        try {
            const r = await api.post("/api/admin/infra/settings/test-alert");
            setRes(r.data);
        } catch (e) { setErr(e?.response?.data?.detail || String(e)); }
        setBusy(false);
    };
    const chip = (label, status) => {
        const ok = status === "sent";
        const skip = (status || "").startsWith("skip") || status === "disabled";
        return (
            <span data-testid={`alert-test-${label}`} className={`text-[11px] font-mono px-2 py-0.5 border ${ok ? "bg-emerald-950 text-emerald-300 border-emerald-900" : skip ? "bg-zinc-900 text-zinc-400 border-zinc-700" : "bg-red-950 text-red-300 border-red-900"}`}>
                {label}: {status}
            </span>
        );
    };
    return (
        <div className="cm-card p-4" data-testid="alert-test-card">
            <div className="flex items-center justify-between gap-3">
                <div>
                    <div className="cm-h3 flex items-center gap-1.5"><Bell size={13} /> Monitor-Alarmierung testen</div>
                    <div className="text-[11px] text-zinc-500 mt-1">
                        Sends a test message immediately through all active channels (Pushover + Email).
                        Konfiguration: <span className="font-mono">pushover_*</span> hier, SMTP unter <span className="font-mono">PMG / Mail</span>.
                    </div>
                </div>
                <button data-testid="alert-test-send" onClick={send} disabled={busy}
                    className="cm-btn-primary text-xs px-3 py-2 disabled:opacity-50 whitespace-nowrap">
                    {busy ? "Sende…" : "Test-Alarm senden"}
                </button>
            </div>
            {(res || err) && (
                <div className="mt-3 flex flex-wrap gap-2 items-center">
                    {err && <span className="text-xs text-cm-danger" data-testid="alert-test-error">{err}</span>}
                    {res && chip("pushover", res.pushover)}
                    {res && chip("email", res.email)}
                </div>
            )}
        </div>
    );
}

// Wildcard-TLS (DNS-01/InternetX) — Status + Trockenlauf/Ausstellung.
// Löst das Let's-Encrypt-Rate-Limit-Problem: ein Wildcard-Cert pro Basis-Domain
// deckt alle Subdomains ab, statt pro Subdomain On-Demand-HTTP-01 auszulösen.
function WildcardTlsPanel() {
    const [busy, setBusy] = useState(null);
    const [err, setErr] = useState(null);
    const [form, setForm] = useState(null);       // Konfig-Formular-State
    const [savedMsg, setSavedMsg] = useState(null);
    const { data, mutate } = useSWR("/api/admin/lb/wildcard-tls/status", fetcher, {
        refreshInterval: (d) => (d?.last_run?.status === "running" ? 4000 : 0),
    });

    // Formular einmalig aus dem Status vorbefüllen (Domains: gesetzte ODER Vorschlag).
    useEffect(() => {
        if (data && form === null) {
            const c = data.config || {};
            setForm({
                internetx_api_user: c.internetx_api_user || "",
                internetx_api_password: "",   // leer = unverändert (nur bei Eingabe gesetzt)
                internetx_context: c.internetx_context || "4",
                wildcard_tls_domains: c.wildcard_tls_domains || data.suggested_domains || "",
                wildcard_tls_enabled: c.wildcard_tls_enabled || "1",
                wildcard_tls_ask_deny: c.wildcard_tls_ask_deny || "0",
            });
        }
    }, [data, form]);

    const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const saveConfig = async () => {
        setBusy("save"); setErr(null); setSavedMsg(null);
        const puts = [
            ["internetx_api_user", form.internetx_api_user.trim()],
            ["internetx_context", form.internetx_context],
            ["wildcard_tls_domains", form.wildcard_tls_domains.trim()],
            ["wildcard_tls_enabled", form.wildcard_tls_enabled],
            ["wildcard_tls_ask_deny", form.wildcard_tls_ask_deny],
        ];
        if (form.internetx_api_password)
            puts.push(["internetx_api_password", form.internetx_api_password]);
        try {
            for (const [k, v] of puts) {
                if (v === "" || v === null || v === undefined) continue;
                await api.put(`/api/admin/infra/settings/${k}`, { value: String(v) });
            }
            setSavedMsg("Configuration saved.");
            setForm((f) => ({ ...f, internetx_api_password: "" }));
            await mutate();
        } catch (e) {
            setErr(`Speichern fehlgeschlagen bei „${e?.config?.url?.split("/").pop() || "?"}": ${e?.response?.data?.detail || String(e)}`);
        } finally {
            setBusy(null);
        }
    };

    const run = async (params, confirmMsg) => {
        if (confirmMsg && !confirm(confirmMsg)) return;
        setBusy(params.staging ? "staging" : (params.force ? "force" : "issue"));
        setErr(null); setSavedMsg(null);
        try {
            await api.post(`/api/admin/lb/wildcard-tls/issue?${new URLSearchParams(params)}`);
            await mutate();
        } catch (e) {
            setErr(e?.response?.data?.detail || String(e));
        } finally {
            setBusy(null);
        }
    };

    const lr = data?.last_run;
    const running = lr?.status === "running";
    const chip = (ok, label) => (
        <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded uppercase border ${ok ? "bg-emerald-950 text-emerald-300 border-emerald-900" : "bg-amber-950 text-amber-300 border-amber-900"}`}>{label}</span>
    );
    const fieldCls = "w-full bg-zinc-950 border border-zinc-700 rounded px-2 py-1.5 text-xs text-zinc-200 focus:outline-none focus:border-sky-600";
    const labelCls = "text-[11px] uppercase tracking-wide text-zinc-500 mb-1 block";

    return (
        <div className="cm-card p-4" data-testid="wildcard-tls-panel">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="cm-h3 flex items-center gap-1.5"><Shield size={13} /> Wildcard-TLS (DNS-01 / InternetX)</div>
                    <div className="text-[11px] text-zinc-500 mt-1 max-w-2xl">
                        Fetches a wildcard certificate per base domain via DNS-01 through InternetX/AutoDNS and loads it into every Caddy LB.
                        Caddy then serves all subdomains from it — without triggering On-Demand/Let's Encrypt per subdomain.
                        Customer custom domains remain untouched.
                    </div>
                </div>
            </div>

            {data && (
                <div className="mt-3 flex flex-wrap gap-2 items-center" data-testid="wildcard-tls-flags">
                    {chip(data.enabled, `worker: ${data.enabled ? "aktiv" : "aus"}`)}
                    {chip(data.configured, `zugangsdaten: ${data.configured ? "gesetzt" : "fehlen"}`)}
                    {chip(data.lego_available, `lego: ${data.lego_available ? "ok" : "fehlt"}`)}
                    {chip(!data.ask_deny, `ask-deny: ${data.ask_deny ? "an" : "aus (empfohlen)"}`)}
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded uppercase border bg-zinc-900 text-zinc-400 border-zinc-700">LBs: {data.lb_count}</span>
                </div>
            )}

            {/* Geführte Konfiguration */}
            {form && (
                <div className="mt-4 border border-zinc-800 rounded-lg p-3 bg-zinc-950/50" data-testid="wildcard-tls-config">
                    <div className="text-xs font-semibold text-zinc-300 flex items-center gap-1.5 mb-3"><SettingsIcon size={12} /> Konfiguration</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <label className={labelCls}>InternetX API-User</label>
                            <input data-testid="wtls-user" className={fieldCls} value={form.internetx_api_user}
                                onChange={(e) => setF("internetx_api_user", e.target.value)} placeholder="AutoDNS API-Benutzer" />
                        </div>
                        <div>
                            <label className={labelCls}>InternetX API password {data?.config?.has_password && <span className="text-emerald-500 normal-case">(set — leave empty = unchanged)</span>}</label>
                            <input data-testid="wtls-password" type="password" className={fieldCls} value={form.internetx_api_password}
                                onChange={(e) => setF("internetx_api_password", e.target.value)} placeholder={data?.config?.has_password ? "••••••••" : "AutoDNS API-Passwort"} />
                        </div>
                        <div>
                            <label className={labelCls}>Context</label>
                            <input data-testid="wtls-context" type="text" inputMode="numeric" className={fieldCls}
                                value={form.internetx_context}
                                onChange={(e) => setF("internetx_context", e.target.value.replace(/[^0-9]/g, ""))}
                                placeholder="z. B. 4 (Live) oder eure Reseller-Context-ID" />
                            <div className="text-[11px] text-zinc-500 mt-1">
                                Eure InternetX AutoDNS Context-ID (numerisch). Standard: 4 = Live, 1 = Demo — als
                                Reseller tragt eure eigene ID ein.
                            </div>
                        </div>
                        <div>
                            <label className={labelCls}>Worker aktiv (Auto-Renew alle 6 h)</label>
                            <select data-testid="wtls-enabled" className={fieldCls} value={form.wildcard_tls_enabled}
                                onChange={(e) => setF("wildcard_tls_enabled", e.target.value)}>
                                <option value="1">An (empfohlen, sobald verifiziert)</option>
                                <option value="0">Off (workers idle — for a controlled first rollout)</option>
                            </select>
                        </div>
                        <div className="md:col-span-2">
                            <label className={labelCls}>Basis-Domains (kommagetrennt) — je <span className="font-mono">*.domain</span> + <span className="font-mono">domain</span></label>
                            <input data-testid="wtls-domains" className={fieldCls} value={form.wildcard_tls_domains}
                                onChange={(e) => setF("wildcard_tls_domains", e.target.value)} placeholder="oncloud.zone, connect.oncloud.zone, souvera.work" />
                            {data?.suggested_domains && (
                                <div className="text-[11px] text-zinc-500 mt-1">
                                    Vorschlag aus deiner Infra: <button type="button" className="text-sky-400 hover:underline font-mono"
                                        onClick={() => setF("wildcard_tls_domains", data.suggested_domains)}>{data.suggested_domains}</button> (click to apply)
                                </div>
                            )}
                        </div>
                        <div className="md:col-span-2">
                            <label className={labelCls}>ask-Deny (Defense-in-Depth)</label>
                            <select data-testid="wtls-askdeny" className={fieldCls} value={form.wildcard_tls_ask_deny}
                                onChange={(e) => setF("wildcard_tls_ask_deny", e.target.value)}>
                                <option value="0">Aus (empfohlen — erst nach Live-Verifikation aktivieren)</option>
                                <option value="1">On (blocks On-Demand for base domains with a loaded wildcard)</option>
                            </select>
                        </div>
                    </div>
                    <div className="mt-3 flex items-center gap-3">
                        <button data-testid="wtls-save" onClick={saveConfig} disabled={busy === "save"}
                            className="cm-btn-primary text-xs px-3 py-2 disabled:opacity-50">
                            {busy === "save" ? "Speichere…" : "Konfiguration speichern"}
                        </button>
                        {savedMsg && <span className="text-xs text-emerald-400" data-testid="wtls-saved">{savedMsg}</span>}
                    </div>
                </div>
            )}

            {data?.certs?.length > 0 && (
                <div className="mt-3 space-y-1" data-testid="wildcard-tls-certs">
                    {data.certs.map((c) => {
                        const dl = c.days_left;
                        const good = dl != null && dl > 30;
                        return (
                            <div key={c.domain} className="flex items-center justify-between gap-2 text-xs font-mono border border-zinc-800 bg-zinc-950 px-2 py-1">
                                <span className="text-zinc-300">*.{c.domain}</span>
                                <span className={good ? "text-emerald-300" : "text-amber-300"}>
                                    {dl != null ? `${Math.round(dl)} days valid` : "—"}
                                </span>
                            </div>
                        );
                    })}
                </div>
            )}

            <div className="mt-4 flex flex-wrap gap-2">
                <button data-testid="wildcard-tls-dryrun" onClick={() => run({ staging: true })}
                    disabled={!!busy || running}
                    className="cm-btn text-xs px-3 py-2 disabled:opacity-50 flex items-center gap-1.5">
                    <FlaskConical size={12} /> {busy === "staging" ? "Starte…" : "Trockenlauf (Staging)"}
                </button>
                <button data-testid="wildcard-tls-issue" onClick={() => run({}, "Issue wildcard certificates NOW via Let's Encrypt PRODUCTION and load them into all LBs?\n\n(Only due/missing ones are fetched anew.)")}
                    disabled={!!busy || running}
                    className="cm-btn-primary text-xs px-3 py-2 disabled:opacity-50 flex items-center gap-1.5">
                    <RefreshCw size={12} /> {busy === "issue" ? "Starte…" : "Jetzt ausstellen + laden"}
                </button>
                <button data-testid="wildcard-tls-force" onClick={() => run({ force: true }, "Neuausstellung ALLER Wildcards ERZWINGEN (verbraucht Let's-Encrypt-Kontingent)?")}
                    disabled={!!busy || running}
                    className="cm-btn text-xs px-3 py-2 disabled:opacity-50">
                    {busy === "force" ? "Starte…" : "Neu erzwingen"}
                </button>
            </div>

            {err && <div className="mt-2 text-xs text-cm-danger" data-testid="wildcard-tls-error">{err}</div>}

            {lr && (
                <div className="mt-3 border-t border-zinc-800 pt-3" data-testid="wildcard-tls-lastrun">
                    <div className="text-[11px] text-zinc-500 flex items-center gap-2">
                        Letzter Lauf: <span className={`font-mono ${running ? "text-sky-300" : lr.status === "error" ? "text-red-300" : "text-zinc-300"}`}>{lr.status}{running && " …"}</span>
                        {lr.params?.staging && chip(false, "staging")}
                        {lr.finished_at && <span>· {new Date(lr.finished_at).toLocaleString()}</span>}
                    </div>
                    {lr.error && <div className="text-xs text-cm-danger mt-1">{lr.error}</div>}
                    {(lr.report?.domains || []).map((d, i) => (
                        <div key={i} className="mt-1 text-[11px] font-mono flex items-start gap-2">
                            {chip(d.ok, d.ok ? d.action || "ok" : "fehler")}
                            <span className="text-zinc-300">*.{d.base}</span>
                            {!d.ok && <span className="text-[#ff9999] break-all">{d.error}</span>}
                            {d.days_left != null && <span className="text-zinc-500">({Math.round(d.days_left)}d)</span>}
                        </div>
                    ))}
                    {(lr.report?.lbs || []).length > 0 && (
                        <div className="mt-2 text-[11px] text-zinc-500">
                            LB-Load: {lr.report.lbs.filter((l) => l.load?.ok).length}/{lr.report.lbs.length} ·
                            ask-deny: {lr.report.lbs.filter((l) => l.ask?.ok).length}/{lr.report.lbs.length} ·
                            geladene Basis-Domains: <span className="font-mono text-zinc-300">{(lr.report.loaded_bases || []).join(", ") || "—"}</span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}



function SettingsPanel() {
    const { data, mutate, error } = useSWR("/api/admin/infra/settings", fetcher);
    const [edits, setEdits] = useState({});  // key → value
    const [saving, setSaving] = useState(null);
    const [savedAt, setSavedAt] = useState(null);
    const [errMsg, setErrMsg] = useState(null);
    const [showWipe, setShowWipe] = useState(false);
    const { data: dbState } = useSWR(showWipe ? "/api/admin/maintenance/state" : null, fetcher);

    const save = async (key) => {
        setSaving(key);
        setErrMsg(null);
        try {
            await api.put(`/api/admin/infra/settings/${key}`, { value: edits[key] });
            setSavedAt(Date.now());
            setEdits((e) => { const c = { ...e }; delete c[key]; return c; });
            await mutate();
        } catch (e) {
            setErrMsg(e?.response?.data?.detail || String(e));
        } finally {
            setSaving(null);
        }
    };

    const onWipe = async (includeResellers) => {
        const magic = "YES_I_WANT_TO_WIPE_TENANT_DATA";
        if (!confirm(`This will DELETE all tenant data${includeResellers ? " + RESELLERS + API tokens" : ""}.\n\nInfrastructure (clusters, pools, service VMs, host networks) is preserved.\n\nProceed?`)) return;
        try {
            const r = await api.post(`/api/admin/maintenance/reset-tenant-data?confirm=${magic}&include_resellers=${includeResellers}`);
            alert(`Wiped ${Object.values(r.data.deleted || {}).reduce((a, b) => a + b, 0)} rows across ${Object.keys(r.data.deleted || {}).length} collections.`);
            mutate();
        } catch (e) {
            alert(e?.response?.data?.detail || String(e));
        }
    };

    if (!data && !error) return <Spinner />;
    return (
        <div className="space-y-4 max-w-3xl">
            <div className="cm-card p-3 text-xs text-zinc-400 bg-zinc-950 border border-zinc-800">
                <strong className="text-zinc-200">Platform Settings</strong> — operator-tuneable knobs that affect cloud provisioning.
            </div>

            <AlertTestCard />

            <WildcardTlsPanel />

            <div className="space-y-3">
                {(data || []).filter((s) => !PMG_KEYS.has(s.key)).map((s) => {
                    const isSet = s.is_secret ? s.is_set : !!s.value;
                    // JSON secrets (e.g. the Firebase FCM service-account key,
                    // ~2.4 KB) don't fit a single-line input — render a
                    // textarea so the full value can be pasted.
                    const multiline = s.key === "souvera_fcm_service_account_json";
                    return (
                    <div key={s.key} className="cm-card p-4" data-testid={`setting-${s.key}`}>
                        <div className="flex items-start justify-between gap-3 mb-2">
                            <div>
                                <div className="cm-h3 font-mono">{s.key}{s.is_secret && <span className="ml-2 text-[10px] uppercase text-zinc-500">secret</span>}</div>
                                <div className="text-[11px] text-zinc-500 mt-1">{s.description}</div>
                            </div>
                            <div className={`text-[10px] px-1.5 py-0.5 rounded uppercase ${isSet ? "bg-emerald-950 text-emerald-300 border border-emerald-900" : "bg-amber-950 text-amber-300 border border-amber-900"}`}>
                                {isSet ? "set" : "unset"}
                            </div>
                        </div>
                        <div className="flex gap-2">
                            {multiline ? (
                            <textarea
                                data-testid={`setting-${s.key}-input`}
                                rows={6}
                                autoComplete="off"
                                spellCheck={false}
                                value={edits[s.key] ?? ""}
                                onChange={(e) => setEdits({ ...edits, [s.key]: e.target.value })}
                                className="cm-input flex-1 font-mono text-[11px] leading-snug"
                                placeholder={isSet ? `current: ${s.value_masked} — paste the full JSON to replace` : "(unset) — paste the Firebase service-account JSON"}
                            />
                            ) : (
                            <input
                                data-testid={`setting-${s.key}-input`}
                                type={s.is_secret ? "password" : "text"}
                                autoComplete={s.is_secret ? "new-password" : "off"}
                                value={s.is_secret ? (edits[s.key] ?? "") : (edits[s.key] ?? s.value ?? "")}
                                onChange={(e) => setEdits({ ...edits, [s.key]: e.target.value })}
                                className="cm-input flex-1 font-mono text-xs"
                                placeholder={s.is_secret ? (isSet ? `current: ${s.value_masked} — type to replace` : "(unset)") : "(unset)"}
                            />
                            )}
                            <button
                                data-testid={`setting-${s.key}-save`}
                                onClick={() => save(s.key)}
                                disabled={saving === s.key || edits[s.key] === undefined || edits[s.key] === "" || (!s.is_secret && edits[s.key] === s.value)}
                                className="cm-btn-primary text-xs px-3 py-2 disabled:opacity-50"
                            >
                                {saving === s.key ? "Saving…" : "Save"}
                            </button>
                        </div>
                        {s.updated_at && (
                            <div className="text-[11px] text-zinc-500 mt-1 font-mono">
                                last updated {s.updated_at}
                            </div>
                        )}
                    </div>
                    );
                })}
            </div>
            {errMsg && <div className="text-red-400 text-xs font-mono break-all" data-testid="settings-error">{errMsg}</div>}
            {savedAt && <div className="text-emerald-300 text-xs">✓ saved</div>}

            {/* DANGER ZONE: DB Reset */}
            <div className="cm-card p-4 border border-red-900 bg-red-950/20 mt-8">
                <div className="cm-h3 text-red-300 mb-2">⚠ Danger Zone — DB Reset</div>
                <div className="text-xs text-zinc-400 mb-3">
                    Wipes all tenant data (clouds, agents, jobs, audit) but PRESERVES infrastructure (clusters, pools, service VMs, host networks).
                    Use before go-live to clear out development fixtures.
                </div>
                <button
                    onClick={() => setShowWipe((v) => !v)}
                    className="cm-btn-ghost text-xs px-3 py-1.5 text-red-300"
                    data-testid="settings-show-wipe"
                >
                    {showWipe ? "Hide" : "Show"} DB state
                </button>
                {showWipe && dbState && (
                    <div className="mt-3 space-y-2">
                        <div className="text-[11px] text-zinc-400">
                            <strong>Will delete:</strong>
                            <pre className="font-mono text-zinc-500 mt-1">{JSON.stringify(dbState.wipeable, null, 2)}</pre>
                        </div>
                        <div className="text-[11px] text-zinc-400">
                            <strong>Will preserve:</strong>
                            <pre className="font-mono text-zinc-500 mt-1">{JSON.stringify(dbState.preserved, null, 2)}</pre>
                        </div>
                        <div className="flex gap-2 mt-3">
                            <button
                                onClick={() => onWipe(false)}
                                className="cm-btn-primary text-xs px-3 py-1.5 bg-red-700 hover:bg-red-600 border-red-600"
                                data-testid="settings-wipe-tenants"
                            >
                                Wipe tenants (keep resellers)
                            </button>
                            <button
                                onClick={() => onWipe(true)}
                                className="cm-btn-primary text-xs px-3 py-1.5 bg-red-800 hover:bg-red-700 border-red-700"
                                data-testid="settings-wipe-all"
                            >
                                Wipe everything (incl. resellers + tokens)
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// =========================================================
// PMG / Mail panel — Proxmox Mail Gateway API creds + the outbound relay
// (smarthost) hosts each Souvera Stalwart sends through.
// =========================================================
function PmgHealthNodes({ health }) {
    const nodes = health?.pmg || [];
    if (!nodes.length) {
        return (
            <div className="cm-card p-3 text-xs text-zinc-500 border border-zinc-800">
                <div className="flex items-center gap-2">
                    <Activity size={12} />
                    <span className="font-mono uppercase tracking-widest">PMG Health</span>
                    <span className="text-zinc-600">— no data (worker not run yet or disabled)</span>
                </div>
            </div>
        );
    }

    const fmtNum = (n) => (n != null ? n.toLocaleString() : "—");
    const fmtBytes = (b) => {
        if (b == null) return "—";
        if (b >= 1073741824) return (b / 1073741824).toFixed(1) + " GB";
        return (b / 1048576).toFixed(0) + " MB";
    };
    const fmtAgo = (iso) => {
        if (!iso) return "—";
        const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
        if (s < 60) return `${Math.round(s)}s`;
        if (s < 3600) return `${Math.round(s / 60)}m`;
        return `${Math.round(s / 3600)}h`;
    };

    return (
        <div className="space-y-2" data-testid="pmg-health-nodes">
            <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">PMG Health</div>
            {nodes.map((n) => {
                const m = HEALTH_META[n.health] || HEALTH_META.unknown;
                const svc = n.services || {};
                const q = n.queue || {};
                const st = n.status || {};
                return (
                    <div key={n.id} className="cm-card p-3 border border-zinc-800" data-testid={`pmg-health-${n.id}`}>
                        <div className="flex items-center justify-between gap-3 mb-2">
                            <div className="flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full ${m.dot}`} />
                                <span className="cm-h3 font-mono text-xs">{n.name}</span>
                                <span data-testid={`pmg-health-${n.id}-badge`}
                                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 border rounded-sm text-[10px] font-mono uppercase ${m.cls}`}>
                                    {m.label}
                                </span>
                            </div>
                            <span className="text-[10px] text-zinc-500 font-mono">probed {fmtAgo(n.last_check)} ago</span>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] font-mono">
                            <div className="text-zinc-500">Services</div>
                            <div className="col-span-1 sm:col-span-3 text-zinc-300">
                                {["postfix", "pmg-smtp-filter"].map((name) => {
                                    const state = svc[name];
                                    const ok = state === "running";
                                    return (
                                        <span key={name} className={`mr-2 ${ok ? "text-emerald-300" : "text-red-400"}`}>
                                            {name}={state || "?"}
                                        </span>
                                    );
                                })}
                            </div>

                            <div className="text-zinc-500">Queue</div>
                            <div className="col-span-1 sm:col-span-3 text-zinc-300">
                                <span className="text-sky-300 mr-2">active={fmtNum(q.active)}</span>
                                <span className={q.deferred > 500 ? "text-orange-300 mr-2" : "text-zinc-400 mr-2"}>deferred={fmtNum(q.deferred)}</span>
                                <span className="text-zinc-500">incoming={fmtNum(q.incoming)}</span>
                            </div>

                            <div className="text-zinc-500">System</div>
                            <div className="col-span-1 sm:col-span-3 text-zinc-300">
                                {st.cpu != null && <span className="mr-2">CPU={(st.cpu * 100).toFixed(1)}%</span>}
                                {st.memory_used != null && <span className="mr-2">RAM={fmtBytes(st.memory_used)}/{fmtBytes(st.memory_total)}</span>}
                                {st.loadavg && Array.isArray(st.loadavg) && st.loadavg.length > 0 && typeof st.loadavg[0] === 'number'
                                    ? <span className="text-zinc-500">load={st.loadavg[0].toFixed(1)}</span>
                                    : st.loadavg != null && <span className="text-zinc-500">load={String(st.loadavg)}</span>}
                            </div>
                        </div>

                        {n.detail && (
                            <div className="mt-2 pt-2 border-t border-zinc-800 text-[11px] text-zinc-500 font-mono">{n.detail}</div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}


function PmgMailPanel() {
    const { data, mutate, error } = useSWR("/api/admin/infra/settings", fetcher);
    const { data: health } = useSWR("/api/admin/infra/health", fetcher, { refreshInterval: 30000 });
    const [edits, setEdits] = useState({});
    const [saving, setSaving] = useState(null);
    const [savedAt, setSavedAt] = useState(null);
    const [errMsg, setErrMsg] = useState(null);

    const save = async (key) => {
        setSaving(key);
        setErrMsg(null);
        try {
            await api.put(`/api/admin/infra/settings/${key}`, { value: edits[key] });
            setSavedAt(Date.now());
            setEdits((e) => { const c = { ...e }; delete c[key]; return c; });
            await mutate();
        } catch (e) {
            setErrMsg(e?.response?.data?.detail || String(e));
        } finally {
            setSaving(null);
        }
    };

    if (!data && !error) return <Spinner />;
    const byKey = Object.fromEntries((data || []).map((s) => [s.key, s]));
    const apiKeys = ["pmg_api_url", "pmg_api_user", "pmg_api_password"];
    const relayKeys = ["pmg_relay_hosts", "pmg_relay_port"];

    const Card = ({ s }) => {
        if (!s) return null;
        const isSet = s.is_secret ? s.is_set : !!s.value;
        return (
            <div className="cm-card p-4" data-testid={`pmg-setting-${s.key}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                        <div className="cm-h3 font-mono">{s.key}{s.is_secret && <span className="ml-2 text-[10px] uppercase text-zinc-500">secret</span>}</div>
                        <div className="text-[11px] text-zinc-500 mt-1">{s.description}</div>
                    </div>
                    <div className={`text-[10px] px-1.5 py-0.5 rounded uppercase ${isSet ? "bg-emerald-950 text-emerald-300 border border-emerald-900" : "bg-amber-950 text-amber-300 border border-amber-900"}`}>
                        {isSet ? "set" : "unset"}
                    </div>
                </div>
                <div className="flex gap-2">
                    <input
                        data-testid={`pmg-setting-${s.key}-input`}
                        type={s.is_secret ? "password" : "text"}
                        autoComplete={s.is_secret ? "new-password" : "off"}
                        value={s.is_secret ? (edits[s.key] ?? "") : (edits[s.key] ?? s.value ?? "")}
                        onChange={(e) => setEdits({ ...edits, [s.key]: e.target.value })}
                        className="cm-input flex-1 font-mono text-xs"
                        placeholder={s.is_secret ? (isSet ? `current: ${s.value_masked} — type to replace` : "(unset)") : "(unset)"}
                    />
                    <button
                        data-testid={`pmg-setting-${s.key}-save`}
                        onClick={() => save(s.key)}
                        disabled={saving === s.key || edits[s.key] === undefined || edits[s.key] === "" || (!s.is_secret && edits[s.key] === s.value)}
                        className="cm-btn-primary text-xs px-3 py-2 disabled:opacity-50"
                    >
                        {saving === s.key ? "Saving…" : "Save"}
                    </button>
                </div>
                {s.updated_at && (
                    <div className="text-[11px] text-zinc-500 mt-1 font-mono">last updated {s.updated_at}</div>
                )}
            </div>
        );
    };

    return (
        <div className="space-y-4 max-w-3xl" data-testid="pmg-mail-panel">
            <div className="cm-card p-3 text-xs text-zinc-400 bg-zinc-950 border border-zinc-800 leading-relaxed">
                <strong className="text-zinc-200">PMG / Mail</strong> — the Proxmox Mail Gateway is the public MX in front of every Souvera mailbox.
                <div className="mt-2 space-y-1 text-zinc-500">
                    <div><span className="text-emerald-300 font-mono">↓ Inbound</span>: Internet → PMG → (egress-gw) → tenant Stalwart <span className="font-mono">:25</span>. The relay-domain + transport are set per cloud automatically.</div>
                    <div><span className="text-sky-300 font-mono">↑ Outbound</span>: Stalwart → PMG <span className="font-mono">:{byKey["pmg_relay_port"]?.value || 26}</span> → Internet. CM adds each Stalwart IP to PMG <span className="font-mono">mynetworks</span> at provision so it may relay, and configures the relay host(s) below as the Stalwart smarthost (failover).</div>
                </div>
            </div>

            <PmgHealthNodes health={health} />

            <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono pt-1">API access</div>
            <div className="space-y-3">{apiKeys.map((k) => <Card key={k} s={byKey[k]} />)}</div>

            <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono pt-3">Outbound relay (smarthost)</div>
            <div className="space-y-3">{relayKeys.map((k) => <Card key={k} s={byKey[k]} />)}</div>

            {errMsg && <div className="text-red-400 text-xs font-mono break-all" data-testid="pmg-settings-error">{errMsg}</div>}
            {savedAt && <div className="text-emerald-300 text-xs">✓ saved</div>}
        </div>
    );
}

// =========================================================
// Proxmox panel
// =========================================================
function ProxmoxPanel() {
    const { data, error, mutate } = useSWR("/api/admin/infra/proxmox", fetcher);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [showSetup, setShowSetup] = useState(false);
    const [testResult, setTestResult] = useState({});
    const [cmpw, setCmpw] = useState(null);
    const [cmpwLoading, setCmpwLoading] = useState(false);
    const [showRegisterVm, setShowRegisterVm] = useState(false);

    const onRevealCmPw = async () => {
        setCmpwLoading(true);
        try {
            const r = await api.get(`/api/admin/infra/cmuser-password`);
            setCmpw(r.data);
        } catch (e) {
            setCmpw({ error: e?.response?.data?.detail || String(e) });
        } finally {
            setCmpwLoading(false);
        }
    };

    const onTest = async (id) => {
        setTestResult((r) => ({ ...r, [id]: { loading: true } }));
        try {
            const r = await api.post(`/api/admin/infra/proxmox/${id}/test`);
            setTestResult((m) => ({ ...m, [id]: r.data }));
        } catch (e) {
            setTestResult((m) => ({ ...m, [id]: { ok: false, error: e?.response?.data?.detail || String(e) } }));
        }
    };

    const onDelete = async (id) => {
        if (!confirm("Delete this Proxmox cluster?")) return;
        await api.delete(`/api/admin/infra/proxmox/${id}`);
        mutate();
    };

    if (!data && !error) return <Spinner />;
    return (
        <div className="space-y-4">
            <div className="flex justify-end gap-2">
                <button
                    data-testid="proxmox-setup-btn"
                    onClick={() => setShowSetup(true)}
                    className="cm-btn-ghost text-xs px-3 py-2 flex items-center gap-1"
                >
                    <FileText size={12} /> Setup guide
                </button>
                <button
                    data-testid="proxmox-add-btn"
                    onClick={() => setShowForm(true)}
                    className="cm-btn-primary text-xs px-3 py-2 flex items-center gap-1"
                >
                    <Plus size={12} /> Add cluster
                </button>
                <button
                    data-testid="proxmox-register-vm-btn"
                    onClick={() => setShowRegisterVm(true)}
                    className="cm-btn-ghost text-xs px-3 py-2 flex items-center gap-1"
                    title="Registriert eine bereits existierende Proxmox-VM als CM-Service-VM"
                >
                    <Server size={12} /> Register existing VM
                </button>
            </div>
            {showSetup && <SetupGuide onClose={() => setShowSetup(false)} />}
            {showForm && <ProxmoxForm onClose={() => { setShowForm(false); mutate(); }} />}
            {showRegisterVm && <RegisterExistingVmForm onClose={() => { setShowRegisterVm(false); mutate(); }} />}
            {editing && <ProxmoxForm existing={editing} onClose={() => { setEditing(null); mutate(); }} />}
            {data?.length === 0 ? (
                <Empty
                    title="No Proxmox cluster registered"
                    hint="Add one to enable VM provisioning, SDN management and template clone."
                />
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {(data || []).map((c) => (
                        <div key={c.id} className="cm-card p-4" data-testid={`proxmox-card-${c.id}`}>
                            <div className="flex items-start justify-between gap-2 mb-2">
                                <div>
                                    <div className="cm-h3">{c.name}</div>
                                    <div className="font-mono text-xs text-zinc-500">{c.api_url}</div>
                                </div>
                                <StatusBadge status={c.status} />
                            </div>
                            <KV k="Token" mono v={c.token_id} />
                            <KV k="Secret" mono v={c.token_secret_masked} />
                            <KV k="VM disk storage (ALL VMs)" mono v={c.storage} />
                            <KV k="Template storage" mono v={c.template_storage || c.snippet_storage || c.storage} />
                            <KV k="Snippet storage" mono v={c.snippet_storage || c.storage} />
                            <KV k="IP Pool" mono v={c.host_network_id ? <IpPoolBadge id={c.host_network_id} /> : <span className="text-amber-400">⚠ none — set in edit</span>} />
                            <KV k="DNS" mono v={c.default_dns || "—"} />
                            <KV k="Templates" mono v={Object.entries(c.template_map || {}).map(([k, v]) => `${k}=${v}`).join(", ") || "—"} />
                            <KV k="Template node (Prep-VM builder)" mono v={c.template_node || <span className="text-amber-400">⚠ none — auto-picks first Control Plane</span>} />
                            <div className="flex gap-2 mt-3 flex-wrap">
                                <button
                                    data-testid={`proxmox-edit-${c.id}`}
                                    onClick={() => setEditing(c)}
                                    className="cm-btn-ghost text-xs px-2 py-1 flex items-center gap-1"
                                >
                                    <FileText size={12} /> Edit
                                </button>
                                <button
                                    data-testid={`proxmox-test-${c.id}`}
                                    onClick={() => onTest(c.id)}
                                    className="cm-btn-ghost text-xs px-2 py-1 flex items-center gap-1"
                                >
                                    <FlaskConical size={12} /> Test
                                </button>
                                <button
                                    data-testid={`proxmox-delete-${c.id}`}
                                    onClick={() => onDelete(c.id)}
                                    className="cm-btn-ghost text-xs px-2 py-1 text-red-400 flex items-center gap-1"
                                >
                                    <Trash2 size={12} /> Delete
                                </button>
                                <button
                                    data-testid={`proxmox-cmuser-pw-${c.id}`}
                                    onClick={onRevealCmPw}
                                    disabled={cmpwLoading}
                                    className="cm-btn-ghost text-xs px-2 py-1 flex items-center gap-1"
                                    title="Reveal the cmuser console/SSH password for debugging VMs"
                                >
                                    <KeyRound size={12} /> {cmpwLoading ? "…" : "Debug login (cmuser)"}
                                </button>
                            </div>
                            {cmpw && (
                                <div className="mt-2 text-xs font-mono p-2 bg-zinc-900 border border-zinc-800" data-testid="cmuser-pw-box">
                                    {cmpw.error ? (
                                        <span className="text-red-400">FAIL: {cmpw.error}</span>
                                    ) : (
                                        <div className="space-y-1">
                                            <div className="text-zinc-500">VM console/SSH login (cloud-init <code>cmuser</code>):</div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-zinc-400">user</span>
                                                <span className="text-zinc-200">{cmpw.username}</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-zinc-400">pass</span>
                                                <span className="text-emerald-300 select-all" data-testid="cmuser-pw-value">{cmpw.password}</span>
                                                <button
                                                    data-testid="cmuser-pw-copy"
                                                    onClick={() => navigator.clipboard?.writeText(cmpw.password || "")}
                                                    className="cm-btn-ghost px-1 py-0.5 flex items-center gap-1"
                                                    title="Copy password"
                                                >
                                                    <Copy size={11} /> copy
                                                </button>
                                            </div>
                                            <div className="text-amber-400/80 text-[11px] mt-1">{cmpw.note}</div>
                                        </div>
                                    )}
                                </div>
                            )}
                            {testResult[c.id] && (
                                <div className={`mt-2 text-xs font-mono p-2 ${testResult[c.id].ok ? "bg-emerald-950 text-emerald-300" : "bg-red-950 text-red-300"}`}>
                                    {testResult[c.id].loading ? "Testing…" :
                                        testResult[c.id].ok ?
                                            `OK · pve ${testResult[c.id].version?.release || ""}` :
                                            `FAIL: ${testResult[c.id].error}`}
                                </div>
                            )}
                            {/* Sprint 1 — per-cluster node inventory + role assignment + auto-SDN */}
                            <NodesManager clusterId={c.id} />
                            {/* Templates moved to a dedicated /templates page (Sprint 23) —
                                listing files per-cluster was duplicating identical shared-NFS
                                content and confusing operators. */}
                        </div>
                    ))}
                </div>
            )}
            {data && data.length > 0 && <OrphanVmsCard />}
        </div>
    );
}

// =========================================================
// Orphaned VMs / Containers — Proxmox guests not tracked by CM
// =========================================================
function OrphanVmsCard() {
    const { data, error, mutate, isValidating } = useSWR(
        "/api/admin/infra/proxmox/orphan-vms", fetcher);
    const [busy, setBusy] = useState({});

    const onDestroy = async (o) => {
        if (!confirm(
            `Permanently delete VM/CT ${o.vmid} (${o.name || "?"}) on node ${o.node}?`
        )) return;
        setBusy((b) => ({ ...b, [o.vmid]: true }));
        const doDestroy = (force) =>
            api.post("/api/admin/infra/proxmox/orphan-vms/destroy", {
                cluster_id: o.cluster_id, vmid: o.vmid, type: o.type, force,
            });
        try {
            await doDestroy(false);
            await mutate();
        } catch (e) {
            const detail = e?.response?.data?.detail || String(e);
            // Proxmox 'protection mode' ist ein bewusster "nicht löschen"-Schutz.
            // Erst ehrlich melden, dann explizit erzwingen lassen.
            if (/protection mode/i.test(detail)) {
                if (confirm(
                    `VM/CT ${o.vmid} is PROTECTED in Proxmox (protection mode). ` +
                    `Remove protection and delete permanently anyway?`
                )) {
                    try {
                        await doDestroy(true);
                        await mutate();
                    } catch (e2) {
                        alert("Deletion failed: " +
                            (e2?.response?.data?.detail || String(e2)));
                    }
                }
            } else {
                alert("Deletion failed: " + detail);
            }
        } finally {
            setBusy((b) => ({ ...b, [o.vmid]: false }));
        }
    };

    const orphans = data?.orphans || [];
    return (
        <div className="cm-card p-4" data-testid="orphan-vms-card">
            <div className="flex items-start justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                    <Boxes size={16} className="text-amber-400" />
                    <div>
                        <div className="cm-h3">Orphaned VMs / Containers</div>
                        <div className="text-xs text-zinc-500">
                            Proxmox guests that belong to no CM record —
                            deletable manually here.
                        </div>
                    </div>
                </div>
                <button
                    data-testid="orphan-vms-refresh"
                    onClick={() => mutate()}
                    className="cm-btn-ghost text-xs px-2 py-1 flex items-center gap-1"
                >
                    <RefreshCw size={12} className={isValidating ? "animate-spin" : ""} />
                    Refresh
                </button>
            </div>
            {error ? (
                <div className="text-xs font-mono p-2 bg-red-950 text-red-300"
                     data-testid="orphan-vms-error">
                    FEHLER: {error?.response?.data?.detail || String(error)}
                </div>
            ) : !data ? (
                <Spinner />
            ) : orphans.length === 0 ? (
                <div className="text-xs text-emerald-400/80"
                     data-testid="orphan-vms-empty">
                    ✓ No orphaned guests — all Proxmox VMs/containers are known to CM.
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid="orphan-vms-table">
                        <thead>
                            <tr className="text-left text-zinc-500 border-b border-zinc-800">
                                <th className="py-1 pr-3">VMID</th>
                                <th className="py-1 pr-3">Name</th>
                                <th className="py-1 pr-3">Typ</th>
                                <th className="py-1 pr-3">Node</th>
                                <th className="py-1 pr-3">Status</th>
                                <th className="py-1 pr-3">Cloud-Tag</th>
                                <th className="py-1 pr-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {orphans.map((o) => (
                                <tr key={`${o.cluster_id}-${o.vmid}`}
                                    className="border-b border-zinc-900"
                                    data-testid={`orphan-vm-row-${o.vmid}`}>
                                    <td className="py-1.5 pr-3 font-mono text-zinc-300">{o.vmid}</td>
                                    <td className="py-1.5 pr-3 text-zinc-300">{o.name || "—"}</td>
                                    <td className="py-1.5 pr-3 font-mono uppercase text-zinc-400">{o.type}</td>
                                    <td className="py-1.5 pr-3 font-mono text-zinc-400">{o.node}</td>
                                    <td className="py-1.5 pr-3">
                                        <span className={o.status === "running" ? "text-emerald-400" : "text-zinc-500"}>
                                            {o.status}
                                        </span>
                                    </td>
                                    <td className="py-1.5 pr-3 font-mono text-[11px]">
                                        {o.cloud_id ? (
                                            <span
                                                className={o.cloud_exists ? "text-amber-400" : "text-zinc-500"}
                                                title={o.cloud_exists
                                                    ? "Cloud existiert noch in CM — VMID dennoch ungetrackt!"
                                                    : "Cloud no longer exists in CM"}>
                                                cloud-{o.cloud_id}{o.cloud_exists ? " ⚠" : ""}
                                            </span>
                                        ) : "—"}
                                    </td>
                                    <td className="py-1.5 pr-3 text-right">
                                        <button
                                            data-testid={`orphan-vm-delete-${o.vmid}`}
                                            disabled={busy[o.vmid]}
                                            onClick={() => onDestroy(o)}
                                            className="cm-btn-ghost text-xs px-2 py-1 text-red-400 flex items-center gap-1 ml-auto"
                                        >
                                            <Trash2 size={12} /> {busy[o.vmid] ? "Deleting…" : "Delete"}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {data.cluster_errors?.length > 0 && (
                        <div className="mt-2 text-[11px] text-amber-400/80">
                            Cluster-Fehler: {data.cluster_errors.map((c) => c.error).join("; ")}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function ProxmoxForm({ onClose, existing }) {
    // `existing` is set when editing — pre-fills form, switches to PATCH.
    const initial = existing ? {
        name: existing.name || "",
        api_url: existing.api_url || "",
        token_id: existing.token_id || "",
        token_secret: "",  // never sent back — only fill if changing
        verify_ssl: !!existing.verify_ssl,
        storage: existing.storage || "ceph01",
        service_vm_storage: existing.service_vm_storage || "",
        snippet_storage: existing.snippet_storage || "cloudmanager",
        default_dns: existing.default_dns || "1.1.1.1,8.8.8.8",
        template_map_str: Object.entries(existing.template_map || {}).map(([k, v]) => `${k}=${v}`).join(","),
        template_node: existing.template_node || "",
        service_vm_bridge: existing.service_vm_bridge || "vmbr0",
        host_network_id: existing.host_network_id || "",
    } : {
        name: "default",
        api_url: "https://pve.example.com:8006",
        token_id: "cloudmanager@pve!cm",
        token_secret: "",
        verify_ssl: false,
        storage: "ceph01",                // VM-disks (tenant + service) live on Ceph
        service_vm_storage: "",           // empty = inherit VM disk storage
        snippet_storage: "cloudmanager",  // templates + cloud-init snippets on shared NFS
        default_dns: "1.1.1.1,8.8.8.8",
        template_map_str: "nc32=101,nc33=102,nc34=103",
        template_node: "",
        service_vm_bridge: "vmbr0",
        host_network_id: "",
    };
    const [form, setForm] = useState(initial);
    const [err, setErr] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        setErr(null);
        const template_map = {};
        for (const part of form.template_map_str.split(",")) {
            const [k, v] = part.split("=").map((s) => s?.trim());
            if (k && v) template_map[k] = parseInt(v, 10);
        }
        const body = {
            ...(existing ? {} : { name: form.name }),
            api_url: form.api_url,
            token_id: form.token_id,
            ...(form.token_secret ? { token_secret: form.token_secret } : {}),
            verify_ssl: form.verify_ssl,
            ...(existing ? {} : { worker_nodes: [] }),
            storage: form.storage,
            service_vm_storage: form.service_vm_storage || "",  // empty = inherit storage
            snippet_storage: form.snippet_storage || form.storage,
            snippet_local_path: "",
            default_dns: form.default_dns,
            template_map,
            template_node: form.template_node || "",
            service_vm_bridge: form.service_vm_bridge,
            host_network_id: form.host_network_id,
        };
        try {
            if (existing) {
                await api.patch(`/api/admin/infra/proxmox/${existing.id}`, body);
            } else {
                await api.post("/api/admin/infra/proxmox", body);
            }
            onClose();
        } catch (e) {
            setErr(e?.response?.data?.detail || String(e));
        }
    };

    return (
        <form onSubmit={submit} className="cm-card p-4 space-y-4" data-testid="proxmox-form">
            <div className="cm-h3 mb-2">{existing ? `Edit cluster — ${existing.name}` : "Register Proxmox cluster"}</div>
            <div className="text-xs text-zinc-500 bg-zinc-950 border border-zinc-800 p-2">
                <strong className="text-zinc-300">{existing ? "Editing live config" : "After saving"}:</strong>{" "}
                {existing
                    ? "Changes apply on next provision job. Leave token-secret blank to keep the existing one."
                    : <>Open the cluster card, click <span className="text-[#CCFF00]">Scan Nodes</span>, then assign each node a role (Control / Worker) and click <span className="text-[#CCFF00]">Setup Networks</span>.</>}
            </div>

            {/* --- Connection --- */}
            <FormSection title="Connection">
                <FieldH label="Name" v={form.name} on={(v) => setForm({ ...form, name: v })} testid="px-name"
                         help="Your label for this Proxmox cluster. Just metadata — used in audit logs and the dashboard."
                         disabled={!!existing} />
                <FieldH label="API URL" v={form.api_url} on={(v) => setForm({ ...form, api_url: v })} testid="px-url"
                         help="Full Proxmox API URL incl. port. Example: https://pve.example.com:8006" />
                <FieldH label="Token ID" v={form.token_id} on={(v) => setForm({ ...form, token_id: v })} testid="px-token-id"
                         help="Proxmox API token in the user@realm!tokenname format." />
                <FieldH label={existing ? "Token secret (leave blank to keep)" : "Token secret"}
                         v={form.token_secret} on={(v) => setForm({ ...form, token_secret: v })}
                         testid="px-token-secret" type="password"
                         help="The secret value shown once when you created the token. Stored encrypted (Fernet) in MariaDB." />
                <label className="flex items-center gap-2 text-xs text-zinc-300">
                    <input type="checkbox" checked={form.verify_ssl} onChange={(e) => setForm({ ...form, verify_ssl: e.target.checked })} data-testid="px-verify-ssl" />
                    Verify SSL
                    <HelpIcon text="Check if your Proxmox uses a publicly trusted certificate." />
                </label>
            </FormSection>

            {/* --- Storage --- */}
            <FormSection title="Storage">
                <FieldH label="VM disk storage — ALL running VMs (Prep, Service, LB, Redis, Tenant)"
                         v={form.storage} on={(v) => setForm({ ...form, storage: v })} testid="px-storage"
                         help="Proxmox storage ID where EVERY running VM disk lives — Prep, Service, LB, Redis, Tenant. Use a shared backend (Ceph RBD recommended, e.g. ceph01) so VMs migrate between nodes and cross-node clones work. This is the single source of truth: there is no per-kind override anymore." />
                <FieldH label="Template & snippet storage (NFS — content-type 'snippets')"
                         v={form.snippet_storage} on={(v) => setForm({ ...form, snippet_storage: v })} testid="px-snip-storage"
                         help="Proxmox storage ID for templates (qcow2) AND cloud-init snippets ONLY. MUST have content-types 'Snippets' and 'Import' enabled. Typically a shared NFS export (Ceph RBD does not support snippets). Example: cloudmanager. Running VM disks do NOT go here." />
            </FormSection>

            {/* --- Service-VM networking --- */}
            <FormSection title="Service-VM networking (Redis / Collabora / Caddy / Notify Push / Talk HPB)">
                <FieldH label="Bridge (host LAN)" v={form.service_vm_bridge} on={(v) => setForm({ ...form, service_vm_bridge: v })} testid="px-svc-bridge"
                         help="Linux bridge service VMs attach to. Usually vmbr0 (host LAN). VMs need Internet for apt-get during bootstrap." />
                <IpPoolPicker value={form.host_network_id} onChange={(v) => setForm({ ...form, host_network_id: v })} />
            </FormSection>

            {/* --- Defaults --- */}
            <FormSection title="Defaults">
                <FieldH label="DNS servers (VM-side)" v={form.default_dns} on={(v) => setForm({ ...form, default_dns: v })} testid="px-dns"
                         help="Comma-separated DNS servers tenant VMs use." />
            </FormSection>

            {/* --- Templates --- */}
            <FormSection title="Templates">
                <FieldH label="Template VMIDs" v={form.template_map_str} on={(v) => setForm({ ...form, template_map_str: v })} testid="px-map"
                         help="Maps each Nextcloud version → Proxmox template VMID. Format: nc32=101,nc33=102,nc34=103" />
                <FieldH label="Template node (where Prep VMs are built — must be Control-Plane)"
                         v={form.template_node}
                         on={(v) => setForm({ ...form, template_node: v })}
                         testid="px-template-node"
                         help="Proxmox node name where the 'Create Prep VMs' workflow clones the debian13 base. MUST be a Control-Plane node (must have vmbr1 + access to the cloudmanager NFS). Leave empty to auto-pick the first online Control-Plane node." />
            </FormSection>

            {err && <div className="text-red-400 text-xs">{err}</div>}
            <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={onClose} className="cm-btn-ghost text-xs px-3 py-1.5">Cancel</button>
                <button type="submit" className="cm-btn-primary text-xs px-3 py-1.5" data-testid="px-submit">{existing ? "Update" : "Save"}</button>
            </div>
        </form>
    );
}

// =========================================================
// Shared Storage (NAS) — NFS over an RBD-backed Debian VM (current model)
// =========================================================
function NasPanel() {
    const { data, mutate, error } = useSWR("/api/admin/infra/nas", fetcher);
    const { data: tmpl } = useSWR("/api/admin/infra/templates/summary", fetcher);
    const baseImage = tmpl?.base_image || null;
    const [edits, setEdits] = useState({});
    const [saving, setSaving] = useState(false);
    const [savedAt, setSavedAt] = useState(null);
    const [errMsg, setErrMsg] = useState(null);
    const [testing, setTesting] = useState(false);
    const [testRes, setTestRes] = useState(null);

    if (error) return <div className="text-red-400 text-sm" data-testid="nas-error">Failed to load NAS config.</div>;
    if (!data) return <div className="text-zinc-500 text-sm">Loading…</div>;

    const g = (k, d = "") => (k in edits ? edits[k] : (data[k] ?? d));
    const set = (k) => (v) => setEdits((e) => ({ ...e, [k]: v }));
    const numOr = (k, d) => { const x = g(k, d); return x === "" || x == null ? d : Number(x); };

    const save = async () => {
        setSaving(true); setErrMsg(null);
        try {
            const body = {
                enabled: !!g("enabled", false),
                rbd_storage: g("rbd_storage", "ceph01"),
                data_quota_gb: numOr("data_quota_gb", 10),
                os_overhead_gb: numOr("os_overhead_gb", 5),
                cores: numOr("cores", 1),
                memory_mb: numOr("memory_mb", 512),
                mount_base: g("mount_base", "/mnt/nc-shared"),
                export_path: g("export_path", "/data"),
                ha: !!g("ha", true),
                ha_group: g("ha_group") || null,
                ha_max_restart: numOr("ha_max_restart", 3),
                ha_max_relocate: numOr("ha_max_relocate", 3),
            };
            await api.put("/api/admin/infra/nas", body);
            setEdits({}); setSavedAt(Date.now()); mutate();
        } catch (e) {
            setErrMsg(e?.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    const runTest = async () => {
        setTesting(true); setTestRes(null);
        try {
            const r = await api.post("/api/admin/infra/nas/test");
            setTestRes(r.data);
        } catch (e) {
            setTestRes({ ok: false, error: e?.response?.data?.detail || "test failed" });
        } finally { setTesting(false); }
    };

    return (
        <div className="max-w-3xl space-y-5" data-testid="nas-panel">
            <div className="text-sm text-zinc-400">
                Shared storage for Nextcloud clouds — the current model. When enabled, every NEW
                cloud gets ONE per-tenant <span className="text-zinc-200 font-mono">NAS</span> (a
                small Debian VM running the kernel NFS server, disk on Ceph RBD), reachable on the
                tenant VXLAN. All web+worker VMs mount it via NFS to share{" "}
                <span className="text-zinc-200 font-mono">config</span>,{" "}
                <span className="text-zinc-200 font-mono">custom_apps</span> and{" "}
                <span className="text-zinc-200 font-mono">themes</span> — so the state SURVIVES
                VM replacement on NC upgrades. No per-VM Ceph NIC, no gateway funnel, no SSH.
            </div>

            <div className="text-[12px] text-amber-300/90 border border-amber-500/30 bg-amber-500/5 p-3 rounded" data-testid="nas-template-hint">
                No template build needed: CM clones your existing{" "}
                <span className="font-mono text-amber-200">debian13</span> VM template (from Infrastructure →
                Template VMIDs) and installs <span className="font-mono text-amber-200">nfs-kernel-server</span>{" "}
                via cloud-init at first boot.
            </div>

            <label className="flex items-center gap-2 text-sm text-zinc-200" data-testid="nas-enabled-row">
                <input type="checkbox" checked={!!g("enabled", false)}
                    onChange={(e) => set("enabled")(e.target.checked)}
                    data-testid="nas-enabled-toggle" />
                Enable shared storage (NAS) for new clouds
            </label>

            <FormSection title="NAS VM (Debian + kernel NFS, disk on Ceph RBD)">
                <div className="text-[13px] text-zinc-300 bg-zinc-900/40 border border-zinc-700/60 rounded p-3" data-testid="nas-vm-template-auto">
                    <div className="text-zinc-400 text-xs mb-1">Base VM template (auto-detected from the Templates page)</div>
                    {baseImage && baseImage.vmid ? (
                        <span className="font-mono text-emerald-300">{baseImage.kind} → VMID {baseImage.vmid}</span>
                    ) : (
                        <span className="text-amber-300">No <span className="font-mono">debian13</span> template registered yet — add it on the Templates page first.</span>
                    )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <FieldH label="RBD storage (VM disk)" v={g("rbd_storage", "ceph01")} on={set("rbd_storage")} testid="nas-rbd"
                        help="Proxmox Ceph RBD storage ID for the NAS VM disk, e.g. ceph01." />
                    <FieldH label="Data quota (GB)" v={g("data_quota_gb", 10)} on={set("data_quota_gb")} testid="nas-quota" type="number"
                        help="Usable size of /data (config/custom_apps/themes are small). VM disk = this + OS overhead." />
                </div>
                <div className="grid grid-cols-3 gap-3">
                    <FieldH label="OS overhead (GB)" v={g("os_overhead_gb", 5)} on={set("os_overhead_gb")} testid="nas-os-gb" type="number" />
                    <FieldH label="vCPU cores" v={g("cores", 1)} on={set("cores")} testid="nas-cores" type="number" />
                    <FieldH label="Memory (MB)" v={g("memory_mb", 512)} on={set("memory_mb")} testid="nas-mem" type="number" />
                </div>
            </FormSection>

            <FormSection title="NFS mount">
                <div className="grid grid-cols-2 gap-3">
                    <FieldH label="Export path (in NAS)" v={g("export_path", "/data")} on={set("export_path")} testid="nas-export" />
                    <FieldH label="Mount base (in NC VMs)" v={g("mount_base", "/mnt/nc-shared")} on={set("mount_base")} testid="nas-mount-base" />
                </div>
            </FormSection>

            <FormSection title="High availability">
                <label className="flex items-center gap-2 text-sm text-zinc-200" data-testid="nas-ha-row">
                    <input type="checkbox" checked={!!g("ha", true)}
                        onChange={(e) => set("ha")(e.target.checked)} data-testid="nas-ha-toggle" />
                    Register HA for the NAS (auto-restart on node failure). Worker + primary web also go HA.
                </label>
                <div className="grid grid-cols-3 gap-3">
                    <FieldH label="HA group (optional)" v={g("ha_group", "")} on={set("ha_group")} testid="nas-ha-group" />
                    <FieldH label="Max restart" v={g("ha_max_restart", 3)} on={set("ha_max_restart")} testid="nas-ha-restart" type="number" />
                    <FieldH label="Max relocate" v={g("ha_max_relocate", 3)} on={set("ha_max_relocate")} testid="nas-ha-relocate" type="number" />
                </div>
            </FormSection>

            <div className="flex items-center gap-3">
                <button onClick={save} disabled={saving} data-testid="nas-save-btn"
                    className="cm-btn-primary disabled:opacity-50">
                    {saving ? "Saving…" : "Save"}
                </button>
                <button onClick={runTest} disabled={testing} data-testid="nas-test-btn"
                    className="cm-btn-secondary flex items-center gap-2">
                    <FlaskConical size={13} /> {testing ? "Checking…" : "Check config"}
                </button>
                {savedAt && <span className="text-[#00cc66] text-xs" data-testid="nas-saved">Saved ✓</span>}
                {errMsg && <span className="text-red-400 text-xs" data-testid="nas-save-err">{errMsg}</span>}
            </div>

            {testRes && (
                <div className={`text-xs font-mono p-3 border ${testRes.ok ? "border-[#00cc66]/40 text-[#00cc66]" : "border-red-500/40 text-red-400"}`}
                    data-testid="nas-test-result">
                    {testRes.ok
                        ? `OK — ${testRes.note || "config complete"}`
                        : `FAILED — ${testRes.error}`}
                </div>
            )}
        </div>
    );
}

function FormSection({ title, children }) {
    return (
        <div className="border-l-2 border-[#CCFF00] pl-3 space-y-2">
            <div className="text-xs text-[#CCFF00] uppercase tracking-widest font-mono">
                {title}
            </div>
            {children}
        </div>
    );
}

function FieldH({ label, v, on, testid, type = "text", help, disabled }) {
    return (
        <label className="block">
            <span className="flex items-center text-xs text-zinc-400 uppercase tracking-wider mb-1">
                {label}
                {help && <HelpIcon text={help} testid={`${testid}-help`} />}
            </span>
            <input
                type={type}
                value={v ?? ""}
                onChange={(e) => on(e.target.value)}
                disabled={disabled}
                data-testid={testid}
                className="cm-input w-full disabled:opacity-50 disabled:cursor-not-allowed"
            />
        </label>
    );
}
// =========================================================
// SDN bridge reconcile + self-heal status (cluster-wide)
// =========================================================
function SdnBridgeReconcile({ clusters }) {
    const list = React.useMemo(() => clusters || [], [clusters]);
    const [clusterId, setClusterId] = useState(list[0]?.id || "");
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [repairing, setRepairing] = useState(false);
    const [err, setErr] = useState(null);
    const { data: autoheal, mutate: mutateHeal } = useSWR(
        "/api/admin/infra/sdn/autoheal-status", fetcher, { refreshInterval: 30000 }
    );

    React.useEffect(() => {
        if (!clusterId && list[0]?.id) setClusterId(list[0].id);
    }, [list, clusterId]);

    const runCheck = async () => {
        if (!clusterId) return;
        setLoading(true); setErr(null);
        try {
            const r = await api.get(`/api/admin/infra/sdn/reconcile?cluster_id=${clusterId}`);
            setReport(r.data);
        } catch (e) {
            setErr(e?.response?.data?.detail || String(e));
        } finally { setLoading(false); }
    };

    const runRepair = async () => {
        if (!clusterId) return;
        if (!window.confirm(
            "SDN reparieren?\n\nFehlende Zonen/VNets/Subnets werden idempotent neu " +
            "gepusht, dann wird die SDN-Config angewandt (PUT /cluster/sdn) und " +
            "gewartet, bis die Bridges auf den Nodes erscheinen."
        )) return;
        setRepairing(true); setErr(null);
        try {
            const r = await api.post("/api/admin/infra/sdn/reconcile", { cluster_id: clusterId });
            setReport(r.data.after);
            mutateHeal();
        } catch (e) {
            setErr(e?.response?.data?.detail || String(e));
        } finally { setRepairing(false); }
    };

    const s = report?.summary;
    const healthy = s?.healthy;
    const lastHeal = (autoheal?.last_run || []).find((x) => x.cluster_id === clusterId);

    return (
        <div className="cm-card p-4" data-testid="sdn-bridge-reconcile">
            <div className="flex items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                    <Activity size={14} className="text-emerald-400" />
                    <span className="cm-h3">Check &amp; repair network (bridges)</span>
                    <HelpIcon text="Compares the SDN zones/VNets/subnets from the CM DB with Proxmox and checks whether the VNet bridge (cmsvc0/cmlbz0/cmtnt0) really exists on EVERY online node. 'Repair' applies the SDN config and waits until the bridges appear via ifreload — prevents 'bridge does not exist' / QEMU exit 1 during deploy." />
                </div>
                <div className="text-xs font-mono text-zinc-500" data-testid="sdn-autoheal-status">
                    Self-Heal: {autoheal?.enabled ? `an · ${autoheal.interval_s}s` : "aus"}
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-3">
                <select
                    data-testid="sdn-reconcile-cluster-select"
                    value={clusterId}
                    onChange={(e) => { setClusterId(e.target.value); setReport(null); }}
                    className="cm-input text-xs py-1.5 px-2 bg-zinc-900 border border-zinc-700 rounded"
                >
                    {list.map((c) => (
                        <option key={c.id} value={c.id}>{c.name} ({c.id?.slice(0, 8)})</option>
                    ))}
                </select>
                <button
                    data-testid="sdn-reconcile-check-btn"
                    onClick={runCheck}
                    disabled={loading || !clusterId}
                    className="cm-btn-ghost text-xs px-3 py-1.5 flex items-center gap-1 disabled:opacity-50"
                >
                    <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Check
                </button>
                <button
                    data-testid="sdn-reconcile-repair-btn"
                    onClick={runRepair}
                    disabled={repairing || !clusterId}
                    className="cm-btn-primary text-xs px-3 py-1.5 flex items-center gap-1 disabled:opacity-50"
                >
                    <Shield size={12} className={repairing ? "animate-pulse" : ""} /> Reparieren &amp; Anwenden
                </button>
            </div>

            {err && <div className="text-xs text-red-300 font-mono break-all mb-2" data-testid="sdn-reconcile-error">{err}</div>}

            {report && (
                <div className="space-y-3" data-testid="sdn-reconcile-report">
                    <div className={`text-xs font-mono px-2 py-1 rounded inline-block ${healthy ? "bg-emerald-900/40 text-emerald-300" : "bg-amber-900/40 text-amber-300"}`}>
                        {healthy ? "✓ Healthy" : "⚠ Drift"} · Nodes: {s.online_node_count} · zones missing: {s.zones_missing} · VNets missing: {s.vnets_missing} · subnets missing: {s.subnets_missing} · bridges missing: {s.bridges_missing}
                    </div>

                    {report.diagnosis && (
                        <div className="border border-amber-700/50 bg-amber-950/30 rounded p-3 text-xs" data-testid="sdn-diagnosis">
                            <div className="text-amber-300 font-semibold mb-1">{report.diagnosis.title}</div>
                            <div className="text-zinc-400 mb-2">{report.diagnosis.cause}{report.diagnosis.bridges?.length ? ` (betroffen: ${report.diagnosis.bridges.join(", ")})` : ""}</div>
                            <div className="text-zinc-300 font-medium mb-1">So behebst du es (auf den Proxmox-Nodes):</div>
                            <ol className="list-decimal ml-5 space-y-0.5 text-zinc-400 font-mono">
                                {report.diagnosis.fix_steps.map((st, i) => (<li key={i}>{st}</li>))}
                            </ol>
                        </div>
                    )}

                    {report.node_bridges && (
                        <div className="text-xs font-mono text-zinc-400">
                            {Object.entries(report.node_bridges).map(([n, br]) => (
                                <div key={n} data-testid={`sdn-node-bridges-${n}`}>{n}: {br.join(", ") || "(keine)"}</div>
                            ))}
                        </div>
                    )}

                    {(report.vnets || []).length > 0 && (
                        <div className="space-y-1">
                            <div className="text-xs uppercase tracking-wide text-zinc-500">VNet-Bridges pro Node</div>
                            {report.vnets.map((v) => (
                                <div key={v.vnet} className="text-xs font-mono flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-zinc-800 py-1" data-testid={`sdn-vnet-row-${v.vnet}`}>
                                    <span className="text-zinc-200 w-20">{v.vnet}</span>
                                    <span className={v.present ? "text-emerald-400" : "text-red-400"}>{v.present ? "vnet ✓" : "vnet ✗"}</span>
                                    <span className={v.subnet_present ? "text-emerald-400" : "text-red-400"}>{v.subnet_present ? "subnet ✓" : "subnet ✗"}</span>
                                    {(v.bridge_ok_nodes || []).map((n) => (
                                        <span key={n} className="text-emerald-400">{n} ✓</span>
                                    ))}
                                    {(v.bridge_missing_nodes || []).map((n) => (
                                        <span key={n} className="text-red-400">{n} ✗ (bridge missing)</span>
                                    ))}
                                </div>
                            ))}
                        </div>
                    )}

                    {(report.zones || []).length > 0 && (
                        <div className="text-xs font-mono text-zinc-400">
                            Zones: {report.zones.map((z) => `${z.zone}${z.present ? "✓" : "✗"}`).join(" · ")}
                        </div>
                    )}
                </div>
            )}

            {lastHeal && (
                <div className="text-xs font-mono text-zinc-500 mt-3" data-testid="sdn-autoheal-last">
                    Last self-heal: {lastHeal.repaired ? "repaired" : "checked"} · {lastHeal.healthy ? "healthy" : "drift"} · {lastHeal.ts}
                    {lastHeal.error ? ` · Fehler: ${lastHeal.error}` : ""}
                </div>
            )}
        </div>
    );
}



// =========================================================
// SDN panel
// =========================================================
function SdnPanel() {
    const { data, error, mutate } = useSWR("/api/admin/infra/sdn", fetcher);
    const { data: pveList } = useSWR("/api/admin/infra/proxmox", fetcher);
    const [showForm, setShowForm] = useState(false);
    const [showEvpn, setShowEvpn] = useState(false);
    const [busy, setBusy] = useState(null);

    const reconcile = async (id) => {
        setBusy(id);
        try {
            await api.post(`/api/admin/infra/sdn/${id}/reconcile`);
            mutate();
        } finally {
            setBusy(null);
        }
    };

    const deleteZone = async (zone) => {
        if (!window.confirm(
            `Delete SDN zone "${zone.zone}" from both CM-DB AND Proxmox?\n\n` +
            `This is a CASCADE delete — all VNets in this zone are also removed. ` +
            `Any VMs attached to those VNets will lose connectivity.\n\n` +
            `Use this to retire legacy VXLAN-zones (cmsvc/cmtnt/cmlbz) after EVPN is up.`
        )) {
            return;
        }
        setBusy(zone.id);
        try {
            await api.delete(
                `/api/admin/infra/sdn/zones/${encodeURIComponent(zone.zone)}`,
                { params: { cluster_id: zone.cluster_id, cascade: true, also_proxmox: true } }
            );
            mutate();
        } catch (e) {
            alert("Delete failed: " + (e?.response?.data?.detail || String(e)));
        } finally {
            setBusy(null);
        }
    };

    if (!data && !error) return <Spinner />;
    return (
        <div className="space-y-4">
            <SdnBridgeReconcile clusters={pveList} />
            <div className="flex justify-end">
                <button
                    data-testid="evpn-bootstrap-btn"
                    onClick={() => setShowEvpn(true)}
                    className="cm-btn-primary text-xs px-3 py-2 flex items-center gap-1"
                    disabled={!pveList?.length}
                >
                    <Network size={12} /> Bootstrap EVPN
                </button>
            </div>
            {!pveList?.length && (
                <div className="cm-card p-3 text-xs text-zinc-400">
                    Register a Proxmox cluster first — the SDN zone lives on top of it.
                </div>
            )}
            {showEvpn && pveList?.length > 0 && (
                <EvpnBootstrapForm clusters={pveList} onClose={() => { setShowEvpn(false); mutate(); }} />
            )}
            {(data || []).length === 0 ? (
                <Empty title="No SDN zone yet" hint="Bootstrap the first zone — peers auto-discover from the cluster status." />
            ) : (
                <div className="space-y-3">
                    {data.map((z) => (
                        <div key={z.id} className="cm-card p-4" data-testid={`sdn-card-${z.id}`}>
                            <div className="flex items-start justify-between gap-2 mb-2">
                                <div>
                                    <div className="cm-h3">{z.zone}</div>
                                    <div className="font-mono text-xs text-zinc-500">
                                        {z.type} · cluster {z.cluster_id?.slice(0, 8)}
                                    </div>
                                </div>
                                <StatusBadge status={z.status} />
                            </div>
                            <KV k="IPAM" mono v={z.ipam} />
                            <KV k="Peers" mono v={(z.peers || []).join(", ")} />
                            <KV k="Last reconciled" mono v={z.last_reconciled_at || "never"} />
                            <KV k="Proxmox state" mono v={z.proxmox_status || "—"} />
                            <button
                                data-testid={`sdn-reconcile-${z.id}`}
                                onClick={() => reconcile(z.id)}
                                disabled={busy === z.id}
                                className="cm-btn-ghost text-xs px-3 py-1.5 mt-3 flex items-center gap-1 disabled:opacity-50"
                            >
                                <RefreshCw size={12} className={busy === z.id ? "animate-spin" : ""} />
                                Reconcile peers now
                            </button>
                            <button
                                data-testid={`sdn-delete-${z.id}`}
                                onClick={() => deleteZone(z)}
                                disabled={busy === z.id}
                                className="cm-btn-ghost text-xs px-3 py-1.5 mt-3 ml-2 flex items-center gap-1 disabled:opacity-50 text-red-300 hover:text-red-200 hover:border-red-700"
                            >
                                <Trash2 size={12} /> Delete zone
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function EvpnBootstrapForm({ clusters, onClose }) {
    const [form, setForm] = useState({
        cluster_id: clusters[0]?.id || "",
        controller_name: "cm-ctrl",
        zone_name: "cmevpn",
        vrf_vxlan: 9000,
        anycast_mac: "0E:CC:CC:CC:CC:01",
        service_zone_vnet: "cmsvc0",
        service_zone_vni: 10000,
        service_zone_subnet: "10.16.0.0/24",
        service_zone_gateway: "10.16.0.1",
    });
    const [err, setErr] = useState(null);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        setErr(null);
        setBusy(true);
        try {
            const res = await api.post("/api/admin/infra/sdn/bootstrap-evpn", {
                cluster_id: form.cluster_id,
                controller_name: form.controller_name,
                zone_name: form.zone_name,
                vrf_vxlan: parseInt(form.vrf_vxlan, 10),
                anycast_mac: form.anycast_mac,
                service_zone_vnet: form.service_zone_vnet,
                service_zone_vni: parseInt(form.service_zone_vni, 10),
                service_zone_subnet: form.service_zone_subnet,
                service_zone_gateway: form.service_zone_gateway,
            });
            setResult(res.data);
        } catch (e) {
            setErr(e?.response?.data?.detail || String(e));
        } finally {
            setBusy(false);
        }
    };

    if (result) {
        return (
            <div className="cm-card p-4 space-y-3 border-emerald-700 bg-emerald-950/20" data-testid="evpn-result">
                <div className="cm-h3 text-emerald-300">EVPN bootstrap complete</div>
                <div className="text-xs text-zinc-300 leading-relaxed">
                    Controller, EVPN-Zone und Service-Zone-VNet wurden in Proxmox angelegt
                    und in der CM-DB persistiert.
                </div>
                <div className="bg-amber-950/30 border border-amber-800 rounded p-3 text-xs text-amber-200 leading-relaxed">
                    <div className="font-semibold mb-1.5">⚠ Manueller Schritt auf jedem PVE-Host (einmalig):</div>
                    <pre className="font-mono text-[11px] bg-black/30 rounded p-2 mt-1 whitespace-pre-wrap">
{`# 1) FRR aktivieren (BGP + BFD)
apt install -y frr frr-pythontools
sed -i 's/^bgpd=no/bgpd=yes/' /etc/frr/daemons
sed -i 's/^bfdd=no/bfdd=yes/'  /etc/frr/daemons
systemctl enable --now frr

# 2) IP-Forwarding einschalten
sysctl -w net.ipv4.ip_forward=1
sysctl -w net.ipv6.conf.all.forwarding=1
echo "net.ipv4.ip_forward=1" > /etc/sysctl.d/99-evpn.conf

# 3) Apply SDN config
pvesh set /cluster/sdn`}
                    </pre>
                    <div className="mt-2">
                        Sobald das gelaufen ist, sieht die <a className="underline text-[#CCFF00]" href="/topology">Topology-Page</a> die
                        EVPN-Zone als <em>active</em>.
                    </div>
                </div>
                <pre className="font-mono text-[11px] text-zinc-400 bg-zinc-950 rounded p-2 max-h-40 overflow-auto">
                    {JSON.stringify(result, null, 2)}
                </pre>
                <div className="flex justify-end">
                    <button onClick={onClose} className="cm-btn-primary text-xs px-3 py-1.5">Close</button>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={submit} className="cm-card p-4 space-y-3 border-[#CCFF00]/40" data-testid="evpn-form">
            <div className="cm-h3 mb-1 flex items-center gap-2">
                <Network size={14} /> Bootstrap EVPN
            </div>
            <div className="text-xs text-zinc-400 bg-zinc-950 border border-zinc-800 rounded p-2 leading-relaxed">
                Erzeugt FRR-Controller (iBGP), EVPN-Zone mit VRF-Leak zu <code>vmbr0</code> + Backend-LAN,
                und die Default-Service-Zone mit Anycast-Gateway. Idempotent — wiederholbar.
                Exit-Nodes werden automatisch auf die Control-Plane-Nodes gesetzt.
            </div>
            <label className="block text-xs text-zinc-400 uppercase tracking-wider">Cluster</label>
            <select
                data-testid="evpn-cluster-select"
                value={form.cluster_id}
                onChange={(e) => setForm({ ...form, cluster_id: e.target.value })}
                className="cm-input w-full"
            >
                {clusters.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <div className="grid grid-cols-2 gap-2">
                <Field label="Controller name" v={form.controller_name}
                       on={(v) => setForm({ ...form, controller_name: v })} testid="evpn-ctrl-name" />
                <Field label="Zone name" v={form.zone_name}
                       on={(v) => setForm({ ...form, zone_name: v })} testid="evpn-zone-name" />
            </div>
            <div className="grid grid-cols-2 gap-2">
                <Field label="VRF-VXLAN VNI (transit)" v={form.vrf_vxlan}
                       on={(v) => setForm({ ...form, vrf_vxlan: v })} testid="evpn-vrf-vxlan" />
                <Field label="Anycast MAC" v={form.anycast_mac}
                       on={(v) => setForm({ ...form, anycast_mac: v })} testid="evpn-anycast-mac" />
            </div>
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 pt-2">Service-zone VNet</div>
            <div className="grid grid-cols-2 gap-2">
                <Field label="VNet name (bridge)" v={form.service_zone_vnet}
                       on={(v) => setForm({ ...form, service_zone_vnet: v })} testid="evpn-svc-vnet" />
                <Field label="VNI" v={form.service_zone_vni}
                       on={(v) => setForm({ ...form, service_zone_vni: v })} testid="evpn-svc-vni" />
            </div>
            <div className="grid grid-cols-2 gap-2">
                <Field label="Subnet (CIDR)" v={form.service_zone_subnet}
                       on={(v) => setForm({ ...form, service_zone_subnet: v })} testid="evpn-svc-subnet" />
                <Field label="Anycast gateway IP" v={form.service_zone_gateway}
                       on={(v) => setForm({ ...form, service_zone_gateway: v })} testid="evpn-svc-gw" />
            </div>
            {err && <div className="text-red-400 text-xs font-mono bg-red-950/30 border border-red-900 p-2 rounded">{err}</div>}
            <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={onClose} className="cm-btn-ghost text-xs px-3 py-1.5">Cancel</button>
                <button type="submit" disabled={busy} className="cm-btn-primary text-xs px-3 py-1.5" data-testid="evpn-submit">
                    {busy ? "Bootstrapping…" : "Bootstrap EVPN"}
                </button>
            </div>
        </form>
    );
}

function SdnBootstrapForm({ clusters, onClose }) {
    const [form, setForm] = useState({
        cluster_id: clusters[0]?.id || "",
        zone: "cmint",
        type: "vxlan",
        ipam: "pve",
        peers_str: "",
    });
    const [err, setErr] = useState(null);
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setErr(null);
        setBusy(true);
        const peers = form.peers_str.split(",").map((s) => s.trim()).filter(Boolean);
        try {
            await api.post("/api/admin/infra/sdn/bootstrap", {
                cluster_id: form.cluster_id,
                zone: form.zone,
                type: form.type,
                ipam: form.ipam,
                peers: peers.length ? peers : null,
            });
            onClose();
        } catch (e) {
            setErr(e?.response?.data?.detail || String(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <form onSubmit={submit} className="cm-card p-4 space-y-2" data-testid="sdn-form">
            <div className="cm-h3 mb-2">Bootstrap SDN zone</div>
            <label className="block text-xs text-zinc-400 uppercase tracking-wider">Cluster</label>
            <select
                data-testid="sdn-cluster-select"
                value={form.cluster_id}
                onChange={(e) => setForm({ ...form, cluster_id: e.target.value })}
                className="cm-input w-full"
            >
                {clusters.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <div className="grid grid-cols-2 gap-2">
                <Field label="Zone name" v={form.zone} on={(v) => setForm({ ...form, zone: v })} testid="sdn-zone" />
                <Field label="Type" v={form.type} on={(v) => setForm({ ...form, type: v })} testid="sdn-type" />
            </div>
            <Field label="IPAM (default pve)" v={form.ipam} on={(v) => setForm({ ...form, ipam: v })} testid="sdn-ipam" />
            <Field
                label="Peers (comma-separated IPs, leave empty for auto-discover)"
                v={form.peers_str}
                on={(v) => setForm({ ...form, peers_str: v })}
                testid="sdn-peers"
            />
            {err && <div className="text-red-400 text-xs">{err}</div>}
            <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={onClose} className="cm-btn-ghost text-xs px-3 py-1.5">Cancel</button>
                <button type="submit" disabled={busy} className="cm-btn-primary text-xs px-3 py-1.5" data-testid="sdn-submit">
                    {busy ? "Bootstrapping…" : "Bootstrap"}
                </button>
            </div>
        </form>
    );
}

// =========================================================
// DB Pool panel
// =========================================================
function DbPoolPanel() {
    const { data, error, mutate } = useSWR("/api/admin/infra/db-pools", fetcher);
    const [showPoolForm, setShowPoolForm] = useState(false);
    const [memberPool, setMemberPool] = useState(null);
    const [grantPool, setGrantPool] = useState(null);
    const [credsPool, setCredsPool] = useState(null);
    const [memberTest, setMemberTest] = useState({});
    const [rebalanceBusy, setRebalanceBusy] = useState(null);

    const onRebalance = async (pool) => {
        setRebalanceBusy(pool.id);
        try {
            // 1) Dry-run → show the plan.
            const dry = await api.post(`/api/admin/infra/db-pools/${pool.id}/rebalance`, { dry_run: true });
            const d = dry.data;
            const dist = (d.distribution || []).map((x) => `• ${x.host}: ${x.target_clouds} Cloud(s)`).join("\n");
            const adoptLine = d.adopted_clouds
                ? `\n\n⚠ Davon ${d.adopted_clouds} verwaiste/importierte Cloud(s) (zeigen auf eine Pool-IP, z.B. die VIP) — werden in den Pool adoptiert.`
                : "";
            if (!d.moves) {
                alert(`Already evenly distributed — ${d.total_clouds} cloud(s), no reassignment needed.\n\nTarget distribution:\n${dist}`);
                return;
            }
            if (!confirm(
                `Redistribute (master load balancing) for pool "${pool.name}"?\n\n` +
                `${d.total_clouds} Cloud(s) gesamt, ${d.moves} werden auf die AKTIVEN Master-IPs verteilt.` +
                adoptLine + `\n\n` +
                `Zielverteilung:\n${dist}\n\n` +
                `Galera replicates synchronously → no data loss. Each moved cloud gets a ` +
                `Agent-Heartbeat (≥1.8.24) den neuen dbhost + dbreplica wird entfernt. Fortfahren?`)) return;
            // 2) Apply.
            const r = await api.post(`/api/admin/infra/db-pools/${pool.id}/rebalance`, { dry_run: false });
            alert(`Fertig: ${r.data.moved} Cloud(s) neu zugewiesen` +
                  (r.data.adopted_clouds ? ` (davon ${r.data.adopted_clouds} adoptiert)` : "") +
                  `. config push triggered.`);
            mutate();
        } catch (e) {
            alert(e?.response?.data?.detail || String(e));
        } finally {
            setRebalanceBusy(null);
        }
    };

    const onDeletePool = async (id) => {
        if (!confirm("Delete this DB pool?")) return;
        try {
            await api.delete(`/api/admin/infra/db-pools/${id}`);
            mutate();
        } catch (e) {
            alert(e?.response?.data?.detail || String(e));
        }
    };
    const onToggleMember = async (poolId, m) => {
        const next = (m.status === "active") ? "disabled" : "active";
        if (next === "disabled" && !confirm(
            `Master ${m.host} deaktivieren?\n\nDeaktivierte Master bekommen KEINE neuen Clouds mehr. ` +
            `Existing clouds stay on it for now — run "Redistribute" afterwards to move them to the ` +
            `active masters. Only then can the master be deleted.`)) return;
        try {
            await api.patch(`/api/admin/infra/db-pools/${poolId}/members/${m.id}`, { status: next });
            mutate();
        } catch (e) {
            alert(e?.response?.data?.detail || String(e));
        }
    };
    const onDeleteMember = async (poolId, mid) => {
        if (!confirm("Remove this master IP?")) return;
        try {
            await api.delete(`/api/admin/infra/db-pools/${poolId}/members/${mid}`);
            mutate();
        } catch (e) {
            alert(e?.response?.data?.detail || String(e));
        }
    };
    const onTestMember = async (poolId, mid) => {
        setMemberTest((t) => ({ ...t, [mid]: { loading: true } }));
        try {
            const r = await api.post(`/api/admin/infra/db-pools/${poolId}/members/${mid}/test`);
            setMemberTest((t) => ({ ...t, [mid]: r.data }));
        } catch (e) {
            setMemberTest((t) => ({ ...t, [mid]: { ok: false, error: e?.response?.data?.detail || String(e) } }));
        }
    };

    if (!data && !error) return <Spinner />;
    return (
        <div className="space-y-4">
            <div className="flex justify-end">
                <button
                    data-testid="db-pool-add-btn"
                    onClick={() => setShowPoolForm(true)}
                    className="cm-btn-primary text-xs px-3 py-2 flex items-center gap-1"
                >
                    <Plus size={12} /> Add pool
                </button>
            </div>
            {showPoolForm && <DbPoolForm onClose={() => { setShowPoolForm(false); mutate(); }} />}
            {memberPool && (
                <DbMemberForm
                    pool={memberPool}
                    onClose={() => { setMemberPool(null); mutate(); }}
                />
            )}
            {credsPool && (
                <DbCredentialsForm
                    pool={credsPool}
                    onClose={() => { setCredsPool(null); mutate(); }}
                />
            )}
            {grantPool && (
                <GrantHelperForm
                    pool={grantPool}
                    onClose={() => { setGrantPool(null); mutate(); }}
                />
            )}
            {(data || []).length === 0 ? (
                <Empty title="No DB pool yet" hint="Add a pool with root credentials, then attach the master IPs." />
            ) : (
                <div className="space-y-4">
                    {data.map((p) => (
                        <div key={p.id} className="cm-card p-4" data-testid={`db-pool-${p.id}`}>
                            <div className="flex items-start justify-between gap-2 mb-2">
                                <div>
                                    <div className="cm-h3">{p.name}</div>
                                    <div className="font-mono text-xs text-zinc-500">
                                        {p.type || "standalone"} · {p.region} · {p.engine} · {p.current_clouds}/{p.max_clouds} clouds
                                    </div>
                                    <div className="font-mono text-xs mt-1">
                                        {p.credentials_set
                                            ? <span className="text-emerald-400" data-testid={`db-pool-creds-${p.id}`}>creds: {p.root_user}@ · pw {p.root_pwd_masked}</span>
                                            : <span className="text-amber-400" data-testid={`db-pool-creds-${p.id}`}>⚠ No root credentials set — provisioning not possible</span>}
                                    </div>
                                </div>
                                <StatusBadge status={p.status} />
                            </div>
                            <div className="space-y-2 mt-2">
                                {(p.members || []).length === 0 ? (
                                    <div className="text-xs text-zinc-500">No master yet — add the first master IP.</div>
                                ) : (
                                    p.members.map((m) => {
                                        const disabled = m.status && m.status !== "active";
                                        return (
                                        <div key={m.id} className={`bg-zinc-950 border p-2 text-xs flex items-center justify-between ${disabled ? "border-zinc-800 opacity-50" : "border-zinc-800"}`} data-testid={`db-member-${m.id}`}>
                                            <div className="font-mono text-zinc-200">
                                                <span className={`mr-2 px-1.5 py-0.5 text-[10px] uppercase ${disabled ? "bg-zinc-800 text-zinc-500" : "bg-indigo-900 text-indigo-300"}`}>
                                                    master
                                                </span>
                                                <span className="mr-2 px-1.5 py-0.5 text-[10px] uppercase bg-zinc-800 text-zinc-300">
                                                    {m.node_name || "node"}
                                                </span>
                                                {m.host}:{m.port}
                                                {disabled && <span className="ml-2 px-1.5 py-0.5 text-[10px] uppercase bg-amber-950 text-amber-400" data-testid={`db-member-disabled-${m.id}`}>disabled</span>}
                                                <span className="ml-2 px-1.5 py-0.5 text-[10px] bg-sky-950 text-sky-300" data-testid={`db-member-count-${m.id}`}>
                                                    {m.cloud_count ?? 0} clouds
                                                </span>
                                            </div>
                                            <div className="flex gap-1">
                                                <button onClick={() => onToggleMember(p.id, m)} className={`cm-btn-ghost text-[11px] px-1.5 py-0.5 ${disabled ? "text-emerald-400" : "text-amber-400"}`} data-testid={`db-member-toggle-${m.id}`} title={disabled ? "Master wieder aktivieren" : "Master deaktivieren (keine neuen Clouds mehr)"}>
                                                    {disabled ? <Power size={10} /> : <Pause size={10} />}
                                                </button>
                                                <button onClick={() => onTestMember(p.id, m.id)} className="cm-btn-ghost text-[11px] px-1.5 py-0.5" data-testid={`db-member-test-${m.id}`}>
                                                    <FlaskConical size={10} />
                                                </button>
                                                <button onClick={() => onDeleteMember(p.id, m.id)} className="cm-btn-ghost text-[11px] px-1.5 py-0.5 text-red-400" data-testid={`db-member-delete-${m.id}`}>
                                                    <Trash2 size={10} />
                                                </button>
                                            </div>
                                        </div>
                                        );
                                    })
                                )}
                                {Object.entries(memberTest).filter(([id]) => (p.members || []).some((m) => m.id === id)).map(([id, r]) => (
                                    <div key={`t-${id}`} className={`text-[11px] font-mono pl-2 ${r.ok ? "text-emerald-300" : "text-red-300"}`}>
                                        {r.loading ? "Testing…" : r.ok ? `OK · ${r.version}` : `FAIL: ${r.error}`}
                                    </div>
                                ))}
                            </div>
                            <div className="flex gap-2 mt-3 flex-wrap">
                                <button
                                    onClick={() => setMemberPool(p)}
                                    disabled={p.type === "standalone" && (p.members || []).length >= 1}
                                    className="cm-btn-ghost text-xs px-3 py-1.5 flex items-center gap-1 disabled:opacity-40"
                                    data-testid={`db-pool-add-member-${p.id}`}
                                >
                                    <Plus size={12} /> Add master IP
                                </button>
                                <button
                                    onClick={() => setCredsPool(p)}
                                    className="cm-btn-ghost text-xs px-3 py-1.5 flex items-center gap-1"
                                    data-testid={`db-pool-creds-btn-${p.id}`}
                                    title="Set the pool's root user/password (applies to all master IPs)"
                                >
                                    <Shield size={12} /> Credentials
                                </button>
                                <button
                                    onClick={() => onRebalance(p)}
                                    disabled={rebalanceBusy === p.id || (p.members || []).length < 2}
                                    className="cm-btn-ghost text-xs px-3 py-1.5 text-sky-300 flex items-center gap-1 disabled:opacity-40"
                                    data-testid={`db-pool-rebalance-${p.id}`}
                                    title="Distributes all existing clouds evenly across the master IPs (Galera-safe)"
                                >
                                    <Database size={12} /> {rebalanceBusy === p.id ? "…" : "Neu verteilen"}
                                </button>
                                <button
                                    onClick={() => setGrantPool(p)}
                                    disabled={(p.members || []).length === 0}
                                    className="cm-btn-ghost text-xs px-3 py-1.5 text-emerald-300 flex items-center gap-1 disabled:opacity-40"
                                    data-testid={`db-pool-grant-helper-${p.id}`}
                                    title="Installs the grant helper (fix for 1044 / MDEV-29167) on all master IPs"
                                >
                                    <Shield size={12} /> Grant-Helfer installieren
                                </button>
                                <button onClick={() => onDeletePool(p.id)} className="cm-btn-ghost text-xs px-3 py-1.5 text-red-400 flex items-center gap-1" data-testid={`db-pool-delete-${p.id}`}>
                                    <Trash2 size={12} /> Delete pool
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function DbPoolForm({ onClose }) {
    const [form, setForm] = useState({ name: "", region: "default", engine: "mariadb", type: "galera", max_clouds: 200, root_user: "root", root_password: "" });
    const [err, setErr] = useState(null);
    const submit = async (e) => {
        e.preventDefault();
        setErr(null);
        try {
            await api.post("/api/admin/infra/db-pools", form);
            onClose();
        } catch (e) {
            setErr(e?.response?.data?.detail || String(e));
        }
    };
    return (
        <form onSubmit={submit} className="cm-card p-4 space-y-2" data-testid="db-pool-form">
            <div className="cm-h3 mb-2">New DB pool</div>
            <div className="text-xs text-zinc-500 mb-2">
                Master load balancing: a pool carries ONE root credential set that applies to all master IPs.
                NexTerminal distributes new clouds evenly across the master IPs.
            </div>
            <Field label="Name" v={form.name} on={(v) => setForm({ ...form, name: v })} testid="dbp-name" />
            <Field label="Engine" v={form.engine} on={(v) => setForm({ ...form, engine: v })} testid="dbp-engine" />
            <label className="block">
                <span className="block text-xs text-zinc-400 uppercase tracking-wider mb-1">Pool type</span>
                <select
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                    className="cm-input w-full"
                    data-testid="dbp-type"
                >
                    <option value="galera">galera — N equivalent masters (CM load-balances clouds)</option>
                    <option value="standalone">standalone — single DB server</option>
                </select>
            </label>
            <Field label="Root user" v={form.root_user} on={(v) => setForm({ ...form, root_user: v })} testid="dbp-user" />
            <Field label="Root password" type="password" v={form.root_password} on={(v) => setForm({ ...form, root_password: v })} testid="dbp-pw" />
            <Field label="Max clouds" type="number" v={form.max_clouds} on={(v) => setForm({ ...form, max_clouds: parseInt(v, 10) || 0 })} testid="dbp-max" />
            {err && <div className="text-red-400 text-xs">{err}</div>}
            <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={onClose} className="cm-btn-ghost text-xs px-3 py-1.5">Cancel</button>
                <button type="submit" className="cm-btn-primary text-xs px-3 py-1.5" data-testid="dbp-submit">Save</button>
            </div>
        </form>
    );
}

function DbCredentialsForm({ pool, onClose }) {
    const [form, setForm] = useState({ root_user: pool.root_user || "root", root_password: "" });
    const [err, setErr] = useState(null);
    const submit = async (e) => {
        e.preventDefault();
        setErr(null);
        try {
            const payload = { root_user: form.root_user };
            if (form.root_password) payload.root_password = form.root_password;
            await api.patch(`/api/admin/infra/db-pools/${pool.id}`, payload);
            onClose();
        } catch (e) {
            setErr(e?.response?.data?.detail || String(e));
        }
    };
    return (
        <form onSubmit={submit} className="cm-card p-4 space-y-2" data-testid="db-creds-form">
            <div className="cm-h3 mb-2">Pool-Credentials — {pool.name}</div>
            <div className="text-xs text-zinc-500 mb-2">
                Root user/password apply to ALL master IPs of this pool. An empty password field leaves the
                existing password unchanged.
            </div>
            <Field label="Root user" v={form.root_user} on={(v) => setForm({ ...form, root_user: v })} testid="dbc-user" />
            <Field label="Root password (empty = unchanged)" type="password" v={form.root_password} on={(v) => setForm({ ...form, root_password: v })} testid="dbc-pw" />
            {err && <div className="text-red-400 text-xs">{err}</div>}
            <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={onClose} className="cm-btn-ghost text-xs px-3 py-1.5">Cancel</button>
                <button type="submit" className="cm-btn-primary text-xs px-3 py-1.5" data-testid="dbc-submit">Save</button>
            </div>
        </form>
    );
}

function DbMemberForm({ pool, onClose }) {
    const isGalera = pool.type === "galera";
    const [form, setForm] = useState({ node_name: "", host: "", port: 3306 });
    const [err, setErr] = useState(null);
    const submit = async (e) => {
        e.preventDefault();
        setErr(null);
        try {
            await api.post(`/api/admin/infra/db-pools/${pool.id}/members`, form);
            onClose();
        } catch (e) {
            setErr(e?.response?.data?.detail || String(e));
        }
    };
    return (
        <form onSubmit={submit} className="cm-card p-4 space-y-2" data-testid="db-member-form">
            <div className="cm-h3 mb-2">Add master IP to {pool.name}</div>
            <div className="text-xs text-zinc-500 mb-2">
                {isGalera
                    ? "Master load balancing: all master IPs are equal and share the pool's root credential set. NexTerminal distributes new clouds evenly across them."
                    : "Standalone pool — only one master allowed. Change pool type to Galera to add more master IPs."}
            </div>
            <Field label="Node name (optional)" v={form.node_name} on={(v) => setForm({ ...form, node_name: v })} testid="dbm-node-name" />
            <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2"><Field label="Host / Master IP" v={form.host} on={(v) => setForm({ ...form, host: v })} testid="dbm-host" /></div>
                <Field label="Port" type="number" v={form.port} on={(v) => setForm({ ...form, port: parseInt(v, 10) || 3306 })} testid="dbm-port" />
            </div>
            {err && <div className="text-red-400 text-xs">{err}</div>}
            <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={onClose} className="cm-btn-ghost text-xs px-3 py-1.5">Cancel</button>
                <button type="submit" className="cm-btn-primary text-xs px-3 py-1.5" data-testid="dbm-submit">Save</button>
            </div>
        </form>
    );
}

// =========================================================
// Grant-helper installer — one-click fix for the 1044 / MDEV-29167
// sub-grant bug. NexTerminal installs `mysql.cm_grant_tenant` on every
// writer using transient DB-root credentials (never stored).
// =========================================================
function GrantHelperForm({ pool, onClose }) {
    const [form, setForm] = useState({ root_user: "", root_password: "" });
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [err, setErr] = useState(null);
    const submit = async (e) => {
        e.preventDefault();
        setErr(null); setResult(null); setBusy(true);
        try {
            const body = {};
            if (form.root_user) body.root_user = form.root_user;
            if (form.root_password) body.root_password = form.root_password;
            const r = await api.post(`/api/admin/infra/db-pools/${pool.id}/install-grant-helper`, body);
            setResult(r.data);
        } catch (e) {
            setErr(e?.response?.data?.detail || String(e));
        } finally {
            setBusy(false);
        }
    };
    return (
        <form onSubmit={submit} className="cm-card p-4 space-y-2" data-testid="grant-helper-form">
            <div className="cm-h3 mb-1">Grant-Helfer installieren · {pool.name}</div>
            <div className="text-xs text-zinc-500 mb-2">
                Fixes the 1044 error (MariaDB MDEV-29167) permanently: NexTerminal creates the
                DEFINER-Prozedur <span className="font-mono text-zinc-300">mysql.cm_grant_tenant</span> auf
                on all writers and grants the CM DB user exactly the required rights — without any manual SQL.
                The <b>root credentials entered below are used only once</b> for this operation
                used and <b>not stored</b>. Leave empty if the writer already runs with root rights
                hinterlegt ist.
            </div>
            <Field label="DB-Root-User (optional)" v={form.root_user} on={(v) => setForm({ ...form, root_user: v })} testid="gh-root-user" />
            <Field label="DB-Root-Passwort (optional)" type="password" v={form.root_password} on={(v) => setForm({ ...form, root_password: v })} testid="gh-root-pw" />
            {result && (
                <div className="space-y-1" data-testid="grant-helper-result">
                    <div className={`text-xs font-mono ${result.ok ? "text-emerald-300" : "text-red-300"}`}>
                        {result.ok ? "✓ Helper installed on all writers" : "✗ At least one writer failed"}
                    </div>
                    {(result.writers || []).map((w, i) => (
                        <div key={i} className={`text-[11px] font-mono pl-2 ${w.ok ? "text-emerald-400" : "text-red-400"}`}>
                            {w.host} {w.node_name ? `(${w.node_name})` : ""}: {w.ok ? `OK → ${w.granted_to}` : `FAIL: ${w.error}`}
                        </div>
                    ))}
                </div>
            )}
            {err && <div className="text-red-400 text-xs" data-testid="grant-helper-error">{err}</div>}
            <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={onClose} className="cm-btn-ghost text-xs px-3 py-1.5">{result?.ok ? "Close" : "Cancel"}</button>
                <button type="submit" disabled={busy} className="cm-btn-primary text-xs px-3 py-1.5 disabled:opacity-50" data-testid="gh-submit">
                    {busy ? "Installiere…" : "Installieren"}
                </button>
            </div>
        </form>
    );
}

// =========================================================
// S3 / Object-storage panel
// =========================================================
function S3PoolPanel() {
    const { data, error, mutate } = useSWR("/api/admin/infra/s3-pools", fetcher);
    const [showPoolForm, setShowPoolForm] = useState(false);
    const [memberPool, setMemberPool] = useState(null);
    const [memberTest, setMemberTest] = useState({});

    const onDeletePool = async (id) => {
        if (!confirm("Delete this S3 pool?")) return;
        try {
            await api.delete(`/api/admin/infra/s3-pools/${id}`);
            mutate();
        } catch (e) {
            alert(e?.response?.data?.detail || String(e));
        }
    };
    const onDeleteMember = async (poolId, mid) => {
        if (!confirm("Remove this member?")) return;
        await api.delete(`/api/admin/infra/s3-pools/${poolId}/members/${mid}`);
        mutate();
    };
    const onTestMember = async (poolId, mid) => {
        setMemberTest((t) => ({ ...t, [mid]: { loading: true } }));
        try {
            const r = await api.post(`/api/admin/infra/s3-pools/${poolId}/members/${mid}/test`);
            setMemberTest((t) => ({ ...t, [mid]: r.data }));
        } catch (e) {
            setMemberTest((t) => ({ ...t, [mid]: { ok: false, error: e?.response?.data?.detail || String(e) } }));
        }
    };

    if (!data && !error) return <Spinner />;
    return (
        <div className="space-y-4">
            <div className="flex justify-end">
                <button
                    data-testid="s3-pool-add-btn"
                    onClick={() => setShowPoolForm(true)}
                    className="cm-btn-primary text-xs px-3 py-2 flex items-center gap-1"
                >
                    <Plus size={12} /> Add pool
                </button>
            </div>
            {showPoolForm && <S3PoolForm onClose={() => { setShowPoolForm(false); mutate(); }} />}
            {memberPool && <S3MemberForm pool={memberPool} onClose={() => { setMemberPool(null); mutate(); }} />}
            {(data || []).length === 0 ? (
                <Empty title="No object-storage pool yet" hint="Add a Garage/MinIO pool — the admin token unlocks per-tenant bucket creation." />
            ) : (
                <div className="space-y-4">
                    {data.map((p) => (
                        <div key={p.id} className="cm-card p-4" data-testid={`s3-pool-${p.id}`}>
                            <div className="flex items-start justify-between gap-2 mb-2">
                                <div>
                                    <div className="cm-h3">{p.name}</div>
                                    <div className="font-mono text-xs text-zinc-500">
                                        {p.provider} · {p.type} · {p.region} · {p.current_clouds}/{p.max_clouds} clouds
                                    </div>
                                </div>
                                <StatusBadge status={p.status} />
                            </div>
                            <div className="space-y-2 mt-2">
                                {(p.members || []).length === 0 ? (
                                    <div className="text-xs text-zinc-500">No members yet — add the Garage endpoint.</div>
                                ) : (
                                    p.members.map((m) => (
                                        <div key={m.id} className="bg-zinc-950 border border-zinc-800 p-2 text-xs flex items-center justify-between" data-testid={`s3-member-${m.id}`}>
                                            <div className="font-mono text-zinc-200 truncate">
                                                <span className="mr-2 px-1.5 py-0.5 text-[10px] uppercase bg-zinc-800 text-zinc-300">{m.node_name || "node"}</span>
                                                {m.endpoint_url}
                                                <span className="ml-2 text-zinc-500">admin {m.admin_endpoint}</span>
                                                <span className="ml-2 text-zinc-500">tok {m.admin_token_masked}</span>
                                            </div>
                                            <div className="flex gap-1 shrink-0">
                                                <button onClick={() => onTestMember(p.id, m.id)} className="cm-btn-ghost text-[11px] px-1.5 py-0.5" data-testid={`s3-member-test-${m.id}`}>
                                                    <FlaskConical size={10} />
                                                </button>
                                                <button onClick={() => onDeleteMember(p.id, m.id)} className="cm-btn-ghost text-[11px] px-1.5 py-0.5 text-red-400" data-testid={`s3-member-delete-${m.id}`}>
                                                    <Trash2 size={10} />
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                )}
                                {Object.entries(memberTest).filter(([id]) => (p.members || []).some((m) => m.id === id)).map(([id, r]) => (
                                    <div key={`t-${id}`} className={`text-[11px] font-mono pl-2 ${r.ok ? "text-emerald-300" : "text-red-300"}`}>
                                        {r.loading ? "Testing…" : r.ok ? `OK · ${JSON.stringify(r.info)}` : `FAIL: ${r.error}`}
                                    </div>
                                ))}
                            </div>
                            <div className="flex gap-2 mt-3">
                                <button
                                    onClick={() => setMemberPool(p)}
                                    disabled={p.type === "standalone" && (p.members || []).length >= 1}
                                    className="cm-btn-ghost text-xs px-3 py-1.5 flex items-center gap-1 disabled:opacity-40"
                                    data-testid={`s3-pool-add-member-${p.id}`}
                                >
                                    <Plus size={12} /> Add {p.type === "cluster" ? "node" : "endpoint"}
                                </button>
                                <button
                                    onClick={() => onDeletePool(p.id)}
                                    className="cm-btn-ghost text-xs px-3 py-1.5 text-red-400 flex items-center gap-1"
                                    data-testid={`s3-pool-delete-${p.id}`}
                                >
                                    <Trash2 size={12} /> Delete pool
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function S3PoolForm({ onClose }) {
    const [form, setForm] = useState({ name: "", region: "de-fra", type: "standalone", provider: "garage", max_clouds: 500 });
    const [err, setErr] = useState(null);
    const submit = async (e) => {
        e.preventDefault();
        setErr(null);
        try {
            await api.post("/api/admin/infra/s3-pools", form);
            onClose();
        } catch (e) {
            setErr(e?.response?.data?.detail || String(e));
        }
    };
    return (
        <form onSubmit={submit} className="cm-card p-4 space-y-2" data-testid="s3-pool-form">
            <div className="cm-h3 mb-2">New object-storage pool</div>
            <Field label="Name" v={form.name} on={(v) => setForm({ ...form, name: v })} testid="s3p-name" />
            <Field label="Region (muss exakt = Garage s3_region sein, z. B. de-fra)" v={form.region} on={(v) => setForm({ ...form, region: v })} testid="s3p-region" />
            <label className="block">
                <span className="block text-xs text-zinc-400 uppercase tracking-wider mb-1">Provider</span>
                <select value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} className="cm-input w-full" data-testid="s3p-provider">
                    <option value="garage">Garage HQ</option>
                    <option value="minio" disabled>MinIO (Phase B)</option>
                    <option value="aws" disabled>AWS S3 (Phase B)</option>
                </select>
            </label>
            <label className="block">
                <span className="block text-xs text-zinc-400 uppercase tracking-wider mb-1">Pool type</span>
                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="cm-input w-full" data-testid="s3p-type">
                    <option value="standalone">standalone — single endpoint</option>
                    <option value="cluster">cluster — multi-node Garage layout</option>
                </select>
            </label>
            <Field label="Max clouds" type="number" v={form.max_clouds} on={(v) => setForm({ ...form, max_clouds: parseInt(v, 10) || 0 })} testid="s3p-max" />
            {err && <div className="text-red-400 text-xs">{err}</div>}
            <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={onClose} className="cm-btn-ghost text-xs px-3 py-1.5">Cancel</button>
                <button type="submit" className="cm-btn-primary text-xs px-3 py-1.5" data-testid="s3p-submit">Save</button>
            </div>
        </form>
    );
}

function S3MemberForm({ pool, onClose }) {
    const [form, setForm] = useState({
        node_name: "",
        endpoint_url: "",
        admin_endpoint: "",
        admin_token: "",
        region: pool.region || "",
    });
    const [err, setErr] = useState(null);
    const submit = async (e) => {
        e.preventDefault();
        setErr(null);
        try {
            await api.post(`/api/admin/infra/s3-pools/${pool.id}/members`, form);
            onClose();
        } catch (e) {
            setErr(e?.response?.data?.detail || String(e));
        }
    };
    return (
        <form onSubmit={submit} className="cm-card p-4 space-y-2" data-testid="s3-member-form">
            <div className="cm-h3 mb-2">Add Garage endpoint to {pool.name}</div>
            <Field label="Node name (optional)" v={form.node_name} on={(v) => setForm({ ...form, node_name: v })} testid="s3m-node-name" />
            <Field label="S3 endpoint URL (data)" v={form.endpoint_url} on={(v) => setForm({ ...form, endpoint_url: v })} testid="s3m-endpoint" />
            <Field label="Admin endpoint URL" v={form.admin_endpoint} on={(v) => setForm({ ...form, admin_endpoint: v })} testid="s3m-admin" />
            <Field label="Admin token (bearer)" type="password" v={form.admin_token} on={(v) => setForm({ ...form, admin_token: v })} testid="s3m-token" />
            <Field label="Region (leer = erbt Pool-Region)" v={form.region} on={(v) => setForm({ ...form, region: v })} testid="s3m-region" />
            {err && <div className="text-red-400 text-xs">{err}</div>}
            <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={onClose} className="cm-btn-ghost text-xs px-3 py-1.5">Cancel</button>
                <button type="submit" className="cm-btn-primary text-xs px-3 py-1.5" data-testid="s3m-submit">Save</button>
            </div>
        </form>
    );
}

// =========================================================
// IP Pools — host-network ranges for service-VM placement
// =========================================================
function IpPoolsPanel() {
    const { data, error, mutate } = useSWR("/api/admin/infra/host-networks", fetcher);
    const { data: clusters } = useSWR("/api/admin/infra/proxmox", fetcher);
    const { data: serviceVms } = useSWR("/api/admin/infra/service-vms?kind=egress-gw", fetcher);
    const [showForm, setShowForm] = useState(false);
    const [showSvcZone, setShowSvcZone] = useState(false);
    const [showTntZone, setShowTntZone] = useState(false);
    const [showEgressGw, setShowEgressGw] = useState(false);
    const [refreshingId, setRefreshingId] = useState(null);
    const [refreshResult, setRefreshResult] = useState(null);
    const [tab, setTab] = useState("service");  // "service" | "tenant" | "public"

    const onDelete = async (id) => {
        if (!confirm("Delete this IP pool?")) return;
        try { await api.delete(`/api/admin/infra/host-networks/${id}`); mutate(); }
        catch (e) { alert(e?.response?.data?.detail || String(e)); }
    };

    const onDeleteServiceVm = async (id, name) => {
        if (!confirm(`Delete service-VM '${name || id}'?\n\nThis destroys the Proxmox VM. For egress-gw redeploy, click the deploy button afterwards.`)) return;
        try {
            await api.delete(`/api/admin/infra/service-vms/${id}`);
            await mutate();
            // also re-trigger the egress-gw list refresh
            window.location.reload();
        } catch (e) { alert(e?.response?.data?.detail || String(e)); }
    };

    const onForceHealth = async (id) => {
        try {
            await api.post(`/api/admin/infra/service-vms/${id}/health`);
            await mutate();
        } catch (e) { alert(e?.response?.data?.detail || String(e)); }
    };

    const onRefreshFirewall = async (vmId) => {
        setRefreshingId(vmId);
        setRefreshResult(null);
        try {
            const r = await api.post(`/api/admin/infra/service-vms/${vmId}/refresh-firewall`);
            setRefreshResult({ ok: true, ...r.data });
        } catch (e) {
            setRefreshResult({ ok: false, error: e?.response?.data?.detail || String(e) });
        } finally {
            setRefreshingId(null);
        }
    };

    if (!data && !error) return <Spinner />;
    const filtered = (data || []).filter(
        (p) => (p.purpose || "public") === tab,
    );
    const egressVms = (serviceVms || []).filter((v) => v.kind === "egress-gw" && v.status !== "destroyed" && v.status !== "error");
    return (
        <div className="space-y-4">
            <div className="cm-card p-3 text-xs text-zinc-400 bg-zinc-950 border border-zinc-800">
                <strong className="text-zinc-200">IP Pools</strong> describe where service-VMs and tenant-VMs get their IPs from.
                <ul className="mt-1 space-y-0.5 ml-3 list-disc">
                    <li><span className="text-emerald-300">Service-Zone pools</span> — internal SDN overlay for Redis/Collabora (no public IP).</li>
                    <li><span className="text-sky-300">Tenant-Zone pools</span> — internal SDN overlay for customer Nextcloud VMs. Cross-zone firewall enforced at egress-gw.</li>
                    <li><span className="text-purple-300">Egress-Uplink pools</span> — internal subnet (e.g. 10.101.0.0/24 on vmbr0) for the Egress-GW eth0. NATs outbound, <strong>no public IP needed.</strong></li>
                    <li><span className="text-amber-300">Public-IP pools</span> — host LAN segment, accessible from the internet. <strong>Loadbalancers only.</strong></li>
                </ul>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
                <div className="flex bg-zinc-950 border border-zinc-800 rounded">
                    {[
                        { id: "service", label: "Service-Zone (SDN)", color: "emerald" },
                        { id: "tenant",  label: "Tenant-Zone (SDN)",  color: "sky" },
                        { id: "egress",  label: "Egress-Uplink",      color: "purple" },
                        { id: "public",  label: "Public-IP (LB)",     color: "amber" },
                    ].map((t) => (
                        <button
                            key={t.id}
                            data-testid={`ip-pool-tab-${t.id}`}
                            onClick={() => setTab(t.id)}
                            className={`text-xs px-3 py-1.5 ${tab === t.id ? `text-${t.color}-300 bg-zinc-900` : "text-zinc-500 hover:text-zinc-200"}`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
                <div className="flex-1" />
                {tab === "service" && (
                    <>
                        <button
                            data-testid="svc-zone-bootstrap-btn"
                            onClick={() => setShowSvcZone(true)}
                            className="cm-btn-primary text-xs px-3 py-2 flex items-center gap-1"
                        >
                            <Plus size={12} /> Bootstrap Service Zone
                        </button>
                        {filtered.length > 0 && (
                            <button
                                data-testid="svc-zone-egress-gw-btn"
                                onClick={() => setShowEgressGw(true)}
                                className="cm-btn-ghost text-xs px-3 py-2 flex items-center gap-1 border border-emerald-700 text-emerald-300"
                                title="Deploy a NAT gateway VM so service-zone VMs can reach the Internet"
                            >
                                <Plus size={12} /> Deploy Egress Gateway
                            </button>
                        )}
                    </>
                )}
                {tab === "tenant" && (
                    <button
                        data-testid="tnt-zone-bootstrap-btn"
                        onClick={() => setShowTntZone(true)}
                        className="cm-btn-primary text-xs px-3 py-2 flex items-center gap-1"
                    >
                        <Plus size={12} /> Bootstrap Tenant Zone
                    </button>
                )}
                {tab === "egress" && (
                    <>
                        <button
                            data-testid="egress-pool-add-btn"
                            onClick={() => setShowForm(true)}
                            className="cm-btn-primary text-xs px-3 py-2 flex items-center gap-1"
                        >
                            <Plus size={12} /> Add egress-uplink pool
                        </button>
                        {filtered.length > 0 && (
                            <button
                                data-testid="egress-tab-deploy-egress-gw-btn"
                                onClick={() => setShowEgressGw(true)}
                                className="cm-btn-ghost text-xs px-3 py-2 flex items-center gap-1 border border-purple-700 text-purple-300"
                                title="Deploy a NAT gateway VM whose eth0 sits on this egress-uplink pool"
                            >
                                <Plus size={12} /> Deploy Egress Gateway
                            </button>
                        )}
                    </>
                )}
                {tab === "public" && (
                    <button
                        data-testid="ip-pool-add-btn"
                        onClick={() => setShowForm(true)}
                        className="cm-btn-primary text-xs px-3 py-2 flex items-center gap-1"
                    >
                        <Plus size={12} /> Add public-IP pool
                    </button>
                )}
            </div>

            {/* Service-zone helper: show existing egress-gw VMs with delete button.
                For tenant-zone setup, operator must DELETE + REDEPLOY the egress-gw here
                so the new VM is born with all 3 NICs. */}
            {tab === "service" && egressVms.length > 0 && (
                <div className="cm-card p-3 text-xs bg-zinc-950 border border-emerald-900">
                    <div className="font-bold text-emerald-200 mb-2">Active Egress-GW VMs</div>
                    <div className="text-zinc-400 mb-2">
                        These VMs route service-zone + tenant-zone traffic out to the Internet (NAT) and enforce the cross-zone firewall.
                        After bootstrapping a new Tenant-Zone, <strong className="text-amber-300">delete + redeploy</strong> the egress-gw here so the new VM gets a NIC into the tenant bridge.
                    </div>
                    {egressVms.map((v) => (
                        <div key={v.id} className="flex items-center justify-between gap-3 py-1 border-t border-zinc-900 first:border-t-0">
                            <div className="flex-1 font-mono text-[11px]">
                                <div className="text-zinc-200">{v.hostname || v.id.slice(0, 12)}</div>
                                <div className="text-zinc-500">vmid={v.vmid} · node={v.node} · public_ip={v.ip_internal} · service_ip={v.ip_secondary} · status={v.status}</div>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    data-testid={`egress-gw-health-${v.id}`}
                                    onClick={() => onForceHealth(v.id)}
                                    className="cm-btn-ghost text-xs px-2 py-1 border border-zinc-700"
                                    title="Re-run health probe — fixes stale 'installing' status"
                                >
                                    Probe
                                </button>
                                <button
                                    data-testid={`egress-gw-delete-${v.id}`}
                                    onClick={() => onDeleteServiceVm(v.id, v.hostname)}
                                    className="cm-btn-ghost text-xs px-2 py-1 border border-red-700 text-red-300 hover:bg-red-950/30"
                                >
                                    <Trash2 size={11} className="inline mr-1" /> Delete
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Tenant-zone helper: prompts operator to refresh firewall after new service-VM deploys */}
            {tab === "tenant" && egressVms.length > 0 && (
                <div className="cm-card p-3 text-xs bg-sky-950/40 border border-sky-800">
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                            <strong className="text-sky-200">Cross-Zone Firewall</strong> — Tenant→Service traffic is gated by iptables on the egress-gw.
                            After every new Redis/Collabora/Caddy deploy, click <em>Refresh Firewall</em> to push updated rules without service interruption.
                            {" "}If the egress-gw was deployed <strong>before</strong> the tenant-zone was bootstrapped, you must
                            <strong className="text-amber-300"> delete + redeploy it on the Service-Zone tab</strong> so the new VM gets its 3rd NIC.
                        </div>
                        <div className="flex flex-col gap-1">
                            {egressVms.map((v) => (
                                <button
                                    key={v.id}
                                    data-testid={`egress-gw-refresh-${v.id}`}
                                    disabled={refreshingId === v.id}
                                    onClick={() => onRefreshFirewall(v.id)}
                                    className="cm-btn-primary text-xs px-2 py-1 disabled:opacity-50"
                                >
                                    {refreshingId === v.id ? "Refreshing…" : `Refresh ${v.hostname || v.id.slice(0, 8)}`}
                                </button>
                            ))}
                        </div>
                    </div>
                    {refreshResult && (
                        <div className={`mt-2 p-2 font-mono text-[11px] break-all ${refreshResult.ok ? "text-emerald-300 bg-emerald-950/50" : "text-red-300 bg-red-950/50"}`} data-testid="egress-gw-refresh-result">
                            {refreshResult.ok ? (
                                <>
                                    ✓ rc={refreshResult.rc} · tenant_subnet={refreshResult.tenant_subnet || "(disabled)"} · allowed_ips=[{(refreshResult.allowed_service_ips || []).join(", ") || "—"}]
                                    {refreshResult.output_tail && <pre className="mt-1 whitespace-pre-wrap text-zinc-500">{refreshResult.output_tail.slice(-600)}</pre>}
                                </>
                            ) : (
                                <>✗ {refreshResult.error}</>
                            )}
                        </div>
                    )}
                </div>
            )}

            {showSvcZone && <ServiceZoneForm clusters={clusters} onClose={() => { setShowSvcZone(false); mutate(); }} />}
            {showTntZone && <TenantZoneForm clusters={clusters} onClose={() => { setShowTntZone(false); mutate(); }} />}
            {showEgressGw && <EgressGwForm clusters={clusters} pools={data} onClose={() => { setShowEgressGw(false); mutate(); }} />}
            {showForm && <IpPoolForm presetPurpose={tab === "egress" ? "egress" : "public"} onClose={() => { setShowForm(false); mutate(); }} />}
            {filtered.length === 0 && !showForm && !showSvcZone && !showTntZone && (
                <div className="cm-card p-6 text-center text-zinc-500 text-xs">
                    {tab === "service"
                        ? "No service-zone pool yet. Click 'Bootstrap Service Zone' above — CM creates the VXLAN VNet + subnet + pool in one shot."
                        : tab === "tenant"
                        ? "No tenant-zone pool yet. Click 'Bootstrap Tenant Zone' above — required before you can provision customer Nextcloud VMs."
                        : tab === "egress"
                        ? "No egress-uplink pool yet. Click 'Add egress-uplink pool' above — an internal subnet (e.g. 10.101.0.0/24 on vmbr0) the Egress-GW eth0 NATs out of. No public IP required."
                        : "No public-IP pool yet. Add one if you intend to deploy a Loadbalancer."}
                </div>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {filtered.map((p) => (
                    <div key={p.id} className="cm-card p-4" data-testid={`ip-pool-${p.id}`}>
                        <div className="flex items-start justify-between mb-2">
                            <div>
                                <div className="cm-h3 flex items-center gap-2">
                                    {p.name}
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase ${
                                        p.purpose === "service"
                                            ? "bg-emerald-950 text-emerald-300 border border-emerald-900"
                                            : p.purpose === "tenant"
                                            ? "bg-sky-950 text-sky-300 border border-sky-900"
                                            : p.purpose === "egress"
                                            ? "bg-purple-950 text-purple-300 border border-purple-900"
                                            : "bg-amber-950 text-amber-300 border border-amber-900"
                                    }`}>
                                        {p.purpose || "public"}
                                    </span>
                                </div>
                                <div className="font-mono text-xs text-zinc-500">{p.subnet}</div>
                            </div>
                            <StatusBadge status={p.status} />
                        </div>
                        <KV k="Gateway" mono v={p.gateway} />
                        <KV k="Range" mono v={`${p.range_first} → ${p.range_last}`} />
                        {p.bridge && <KV k="Bridge / VNet" mono v={p.bridge} />}
                        <KV k="DNS" mono v={p.dns_servers || "—"} />
                        <KV k="Used" mono v={`${p.used_count || 0} VM${(p.used_count || 0) === 1 ? "" : "s"}`} />
                        {p.purpose === "tenant" && p.tenant_count !== undefined && (
                            <KV k="Tenants" mono v={`${p.tenant_count} cloud${p.tenant_count === 1 ? "" : "s"}`} />
                        )}
                        {(p.purpose || "public") === "public" && (
                            <PrepIpReservationsPanel poolId={p.id} />
                        )}
                        <div className="flex gap-2 mt-3">
                            <button onClick={() => onDelete(p.id)} data-testid={`ip-pool-delete-${p.id}`} className="cm-btn-ghost text-xs px-2 py-1 text-red-400 flex items-center gap-1">
                                <Trash2 size={12} /> Delete
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────
// Prep-VM IP reservations panel (rendered inside each Public-IP pool
// card). Lists the IPs currently held by Prep VMs (build-time
// artifacts) with a per-row "Release" button + a bulk "Release all"
// shortcut. The IPs are auto-released by the `prep_ip_reaper` worker
// when their VMID is promoted to a Template, but the manual buttons
// stay for edge cases (operator destroyed the VM without promoting).
// ─────────────────────────────────────────────────────────────────
function PrepIpReservationsPanel({ poolId }) {
    const { data, mutate } = useSWR(
        `/api/admin/infra/host-networks/${poolId}/prep-reservations`,
        fetcher,
    );
    const [busy, setBusy] = useState(false);

    const release = async (resId, ip) => {
        if (!window.confirm(
            `Release Prep-VM IP ${ip} back to the free pool?\n\n` +
            `Use this AFTER you've promoted the prep VM to a template ` +
            `(qm template <vmid>) or destroyed it. The IP can then be ` +
            `picked by the next prep batch or pinned to a Loadbalancer.`
        )) return;
        setBusy(true);
        try {
            await api.delete(
                `/api/admin/infra/host-networks/${poolId}/prep-reservations/${resId}`,
            );
            await mutate();
        } catch (e) {
            alert("Release failed: " + (e?.response?.data?.detail || e.message));
        } finally {
            setBusy(false);
        }
    };

    const releaseAll = async () => {
        if (!window.confirm(
            `Release ALL ${data?.length || 0} prep-VM IP reservations on this pool?`,
        )) return;
        setBusy(true);
        try {
            await api.post(
                `/api/admin/infra/host-networks/${poolId}/prep-reservations/release-all`,
            );
            await mutate();
        } catch (e) {
            alert("Release-all failed: " + (e?.response?.data?.detail || e.message));
        } finally {
            setBusy(false);
        }
    };

    if (!data) return null;
    if (!data.length) {
        return (
            <div
                data-testid={`prep-ip-reservations-${poolId}-empty`}
                className="mt-2 text-[11px] font-mono text-zinc-600 italic"
            >
                No active Prep-VM IP reservations.
            </div>
        );
    }
    return (
        <div
            data-testid={`prep-ip-reservations-${poolId}`}
            className="mt-3 border border-amber-900/40 bg-amber-950/20 rounded p-2 space-y-1"
        >
            <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] uppercase tracking-wider text-amber-300 font-semibold">
                    Prep-VM IP reservations ({data.length})
                </span>
                <button
                    data-testid={`prep-ip-release-all-${poolId}`}
                    onClick={releaseAll}
                    disabled={busy}
                    className="text-[11px] text-amber-300 hover:text-amber-200 underline disabled:opacity-40"
                    title="Release every prep-VM reservation on this pool"
                >
                    Release all
                </button>
            </div>
            <div className="space-y-0.5">
                {data.map((r) => (
                    <div
                        key={r.id}
                        data-testid={`prep-ip-reservation-${r.id}`}
                        className="flex items-center justify-between gap-2 text-[11px] font-mono py-0.5"
                    >
                        <div className="text-amber-100 truncate">
                            <span className="text-amber-300">{r.ip}</span>
                            <span className="text-zinc-500"> · </span>
                            <span>{r.vm_name || `${r.kind}#${r.vmid}`}</span>
                            <span className="text-zinc-500"> · </span>
                            <span className="text-zinc-400">nc{r.version}</span>
                            <span className="text-zinc-500"> · </span>
                            <span className="text-zinc-500">{r.node}</span>
                        </div>
                        <button
                            data-testid={`prep-ip-release-${r.id}`}
                            onClick={() => release(r.id, r.ip)}
                            disabled={busy}
                            className="text-[11px] text-zinc-400 hover:text-red-300 underline disabled:opacity-40 shrink-0"
                            title="Release this IP back to the free pool"
                        >
                            Release
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}

function ServiceZoneForm({ clusters, onClose }) {
    const [form, setForm] = useState({
        cluster_id: clusters?.[0]?.id || "",
        zone: "cmsvc",
        vnet: "cmsvc0",   // Proxmox SDN: max 8 chars, alphanumeric only
        vni: 10000,
        subnet: "10.16.0.0/24",
        gateway: "10.16.0.1",
        range_first: "10.16.0.10",
        range_last: "10.16.0.250",
        pool_name: "service-zone",
        mtu: 8950,
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [progress, setProgress] = useState(null);

    // Auto-pick first cluster once loaded
    if (!form.cluster_id && clusters?.[0]?.id) {
        setForm((f) => ({ ...f, cluster_id: clusters[0].id }));
    }

    const submit = async (e) => {
        e.preventDefault();
        setErr(null);
        setBusy(true);
        setProgress("Pushing VNet + Subnet to Proxmox SDN…");
        try {
            await api.post("/api/admin/infra/service-zone/bootstrap", form);
            setProgress("Service zone live ✓ — pool created.");
            setTimeout(onClose, 1200);
        } catch (e) {
            const raw = e?.response?.data?.detail ?? e?.message ?? e;
            setErr(typeof raw === "string" ? raw : JSON.stringify(raw));
            setProgress(null);
        } finally {
            setBusy(false);
        }
    };

    return (
        <form onSubmit={submit} className="cm-card p-4 space-y-2 border border-emerald-700" data-testid="svc-zone-form">
            <div className="cm-h3 mb-2">Bootstrap Service Zone (1-click)</div>
            <div className="text-xs text-zinc-400 bg-zinc-950 border border-zinc-800 p-2 mb-2">
                NexTerminal creates a VXLAN VNet inside the <code>cmsvc</code> SDN zone,
                attaches the subnet below, and registers an IP-pool for service VMs (Redis, Collabora).
                MTU is set to 8950 (Jumbo Frames - 50B VXLAN overhead). Idempotent — re-running is safe.
            </div>
            <label className="block">
                <span className="block text-xs text-zinc-400 uppercase tracking-wider mb-1">Cluster</span>
                <select value={form.cluster_id} onChange={(e) => setForm({ ...form, cluster_id: e.target.value })} className="cm-input w-full" data-testid="svcz-cluster">
                    {(clusters || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
            </label>
            <div className="grid grid-cols-2 gap-2">
                <Field label="VNet name" v={form.vnet} on={(v) => setForm({ ...form, vnet: v })} testid="svcz-vnet" />
                <Field label="VXLAN VNI" type="number" v={form.vni} on={(v) => setForm({ ...form, vni: parseInt(v, 10) || 10000 })} testid="svcz-vni" />
            </div>
            <Field label="Subnet CIDR" v={form.subnet} on={(v) => setForm({ ...form, subnet: v })} testid="svcz-subnet" />
            <div className="grid grid-cols-3 gap-2">
                <Field label="Gateway IP" v={form.gateway} on={(v) => setForm({ ...form, gateway: v })} testid="svcz-gw" />
                <Field label="Range first" v={form.range_first} on={(v) => setForm({ ...form, range_first: v })} testid="svcz-first" />
                <Field label="Range last" v={form.range_last} on={(v) => setForm({ ...form, range_last: v })} testid="svcz-last" />
            </div>
            <Field label="Pool name" v={form.pool_name} on={(v) => setForm({ ...form, pool_name: v })} testid="svcz-pool-name" />
            {progress && <div className="text-emerald-300 text-xs font-mono">⏳ {progress}</div>}
            {err && <div className="text-red-400 text-xs font-mono break-all" data-testid="svcz-error">{err}</div>}
            <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={onClose} className="cm-btn-ghost text-xs px-3 py-1.5">Cancel</button>
                <button type="submit" disabled={busy || !form.cluster_id} className="cm-btn-primary text-xs px-3 py-1.5 disabled:opacity-50" data-testid="svcz-submit">
                    {busy ? "Provisioning…" : "Bootstrap"}
                </button>
            </div>
        </form>
    );
}

// One-click Tenant-Zone bootstrap (analog Service-Zone)
function TenantZoneForm({ clusters, onClose }) {
    const [form, setForm] = useState({
        cluster_id: clusters?.[0]?.id || "",
        zone: "cmtnt",
        vnet: "cmtnt0",
        vni: 20000,
        subnet: "10.20.0.0/16",
        gateway: "10.20.0.1",
        range_first: "10.20.0.10",
        range_last: "10.20.255.250",
        pool_name: "tenant-zone",
        mtu: 8950,
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [progress, setProgress] = useState(null);

    if (!form.cluster_id && clusters?.[0]?.id) {
        setForm((f) => ({ ...f, cluster_id: clusters[0].id }));
    }

    const submit = async (e) => {
        e.preventDefault();
        setErr(null);
        setBusy(true);
        setProgress("Creating SDN zone + VNet + Subnet…");
        try {
            await api.post("/api/admin/infra/tenant-zone/bootstrap", form);
            setProgress("Tenant zone live ✓ — now refresh the egress-gw to wire up the cross-zone firewall.");
            setTimeout(onClose, 2000);
        } catch (e) {
            const raw = e?.response?.data?.detail ?? e?.message ?? e;
            setErr(typeof raw === "string" ? raw : JSON.stringify(raw));
            setProgress(null);
        } finally {
            setBusy(false);
        }
    };

    return (
        <form onSubmit={submit} className="cm-card p-4 space-y-2 border border-sky-700" data-testid="tnt-zone-form">
            <div className="cm-h3 mb-2">Bootstrap Tenant Zone (1-click)</div>
            <div className="text-xs text-zinc-400 bg-zinc-950 border border-zinc-800 p-2 mb-2">
                Creates a separate VXLAN VNet inside the <code>cmtnt</code> SDN zone for customer Nextcloud VMs.
                <strong className="text-sky-300"> Distinct from the service-zone</strong> — cross-zone traffic is
                firewalled at the egress-gw (only Redis 6379, Collabora 9980, LB 80/443 allowed).
                Default subnet <code>10.20.0.0/16</code> = up to ~65k tenant IPs. MTU 8950 (jumbo underlay).
                <br />
                <span className="text-amber-300">After bootstrap, click &quot;Refresh Firewall&quot; on the egress-gw</span> to
                wire up cross-zone routing without downtime.
            </div>
            <label className="block">
                <span className="block text-xs text-zinc-400 uppercase tracking-wider mb-1">Cluster</span>
                <select value={form.cluster_id} onChange={(e) => setForm({ ...form, cluster_id: e.target.value })} className="cm-input w-full" data-testid="tntz-cluster">
                    {(clusters || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
            </label>
            <div className="grid grid-cols-2 gap-2">
                <Field label="VNet name" v={form.vnet} on={(v) => setForm({ ...form, vnet: v })} testid="tntz-vnet" />
                <Field label="VXLAN VNI" type="number" v={form.vni} on={(v) => setForm({ ...form, vni: parseInt(v, 10) || 20000 })} testid="tntz-vni" />
            </div>
            <Field label="Subnet CIDR" v={form.subnet} on={(v) => setForm({ ...form, subnet: v })} testid="tntz-subnet" />
            <div className="grid grid-cols-3 gap-2">
                <Field label="Gateway IP" v={form.gateway} on={(v) => setForm({ ...form, gateway: v })} testid="tntz-gw" />
                <Field label="Range first" v={form.range_first} on={(v) => setForm({ ...form, range_first: v })} testid="tntz-first" />
                <Field label="Range last" v={form.range_last} on={(v) => setForm({ ...form, range_last: v })} testid="tntz-last" />
            </div>
            <Field label="Pool name" v={form.pool_name} on={(v) => setForm({ ...form, pool_name: v })} testid="tntz-pool-name" />
            {progress && <div className="text-sky-300 text-xs font-mono">⏳ {progress}</div>}
            {err && <div className="text-red-400 text-xs font-mono break-all" data-testid="tntz-error">{err}</div>}
            <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={onClose} className="cm-btn-ghost text-xs px-3 py-1.5">Cancel</button>
                <button type="submit" disabled={busy || !form.cluster_id} className="cm-btn-primary text-xs px-3 py-1.5 disabled:opacity-50" data-testid="tntz-submit">
                    {busy ? "Provisioning…" : "Bootstrap"}
                </button>
            </div>
        </form>
    );
}

// One-click Egress-GW deployment (eth0 on an egress-uplink/public pool, + cmsvc0/cmtnt0)
function EgressGwForm({ clusters, pools, onClose }) {
    // eth0 may sit on a dedicated internal egress-uplink pool (preferred,
    // saves a public IP) OR a legacy public-IP pool. Egress pools are
    // listed first so they're the default selection.
    const eth0Pools = (pools || []).filter(
        (p) => (p.purpose || "public") === "egress" || (p.purpose || "public") === "public",
    ).sort((a, b) => {
        const rank = (p) => ((p.purpose || "public") === "egress" ? 0 : 1);
        return rank(a) - rank(b);
    });
    const [form, setForm] = useState({
        cluster_id: clusters?.[0]?.id || "",
        host_network_id: eth0Pools[0]?.id || "",
        cpu: 1,
        ram_mb: 512,
        disk_gb: 5,
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [progress, setProgress] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        setErr(null);
        setBusy(true);
        setProgress("Cloning Debian-13 with NICs: eth0=public, eth1=cmsvc0 (service), eth2=cmtnt0 (tenant, if bootstrapped)…");
        try {
            await api.post("/api/admin/infra/service-vms", {
                cluster_id: form.cluster_id,
                kind: "egress-gw",
                host_network_id: form.host_network_id,
                cpu: form.cpu,
                ram_mb: form.ram_mb,
                disk_gb: form.disk_gb,
            });
            setProgress("Egress-GW provisioning… NAT + cross-zone firewall installs in ~60-90s.");
            setTimeout(onClose, 1800);
        } catch (e) {
            const raw = e?.response?.data?.detail ?? e?.message ?? e;
            setErr(typeof raw === "string" ? raw : JSON.stringify(raw));
            setProgress(null);
        } finally {
            setBusy(false);
        }
    };

    return (
        <form onSubmit={submit} className="cm-card p-4 space-y-2 border border-emerald-700" data-testid="egress-gw-form">
            <div className="cm-h3 mb-2">Deploy Egress Gateway (NAT + cross-zone router)</div>
            <div className="text-xs text-zinc-400 bg-zinc-950 border border-zinc-800 p-2 mb-2">
                A small Debian-13 VM with up to <strong>3 NICs</strong>: eth0 = Internet egress (egress-uplink pool on <code>vmbr0</code>, or a public-IP pool),
                eth1 = service-zone (e.g. <code>10.16.0.1</code>), eth2 = tenant-zone (e.g. <code>10.20.0.1</code>, only if Tenant-Zone is bootstrapped).
                It NATs service-zone & tenant-zone traffic out via eth0,
                and enforces the <strong className="text-sky-300">cross-zone firewall</strong> (tenant→service: only Redis 6379, Collabora 9980, LB 80/443).
                <br />
                <span className="text-amber-300">Deploy this BEFORE any other service VM</span> — without it,
                service-zone VMs have no Internet.
            </div>
            <label className="block">
                <span className="block text-xs text-zinc-400 uppercase tracking-wider mb-1">Cluster</span>
                <select value={form.cluster_id} onChange={(e) => setForm({ ...form, cluster_id: e.target.value })} className="cm-input w-full" data-testid="egw-cluster">
                    {(clusters || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
            </label>
            <label className="block">
                <span className="block text-xs text-zinc-400 uppercase tracking-wider mb-1">eth0 Pool (Internet egress)</span>
                <select value={form.host_network_id} onChange={(e) => setForm({ ...form, host_network_id: e.target.value })} className="cm-input w-full" data-testid="egw-public-pool">
                    {eth0Pools.length === 0 && <option value="">— no egress-uplink or public pool defined —</option>}
                    {eth0Pools.map((p) => {
                        const purp = (p.purpose || "public") === "egress" ? "egress-uplink" : "public";
                        const optLabel = `${p.name} [${purp}] — ${p.subnet}${p.bridge ? `  →  ${p.bridge}` : ""}`;
                        return <option key={p.id} value={p.id}>{optLabel}</option>;
                    })}
                </select>
                {(() => {
                    const selected = eth0Pools.find((p) => p.id === form.host_network_id);
                    if (!selected) return null;
                    const isEgress = (selected.purpose || "public") === "egress";
                    return (
                        <div className="mt-1 text-[11px] font-mono text-zinc-500">
                            eth0 will attach to bridge{" "}
                            <span className="text-emerald-300">
                                {selected.bridge || "vmbr0 (cluster default)"}
                            </span>
                            {" · "}gw{" "}
                            <span className="text-emerald-300">{selected.gateway}</span>
                            {isEgress
                                ? <span className="text-purple-300"> — internal egress-uplink (no public IP)</span>
                                : !selected.bridge && (
                                    <span className="text-amber-400">
                                        {" "}— consider setting bridge explicitly on the pool
                                    </span>
                                )}
                        </div>
                    );
                })()}
            </label>
            <div className="grid grid-cols-3 gap-2">
                <Field label="CPU cores" type="number" min={1} v={form.cpu} on={(v) => setForm({ ...form, cpu: parseInt(v, 10) || 1 })} testid="egw-cpu" />
                <Field label="RAM (MB)" type="number" min={256} v={form.ram_mb} on={(v) => setForm({ ...form, ram_mb: parseInt(v, 10) || 512 })} testid="egw-ram" />
                <Field label="Disk (GB)" type="number" min={3} v={form.disk_gb} on={(v) => setForm({ ...form, disk_gb: parseInt(v, 10) || 5 })} testid="egw-disk" />
            </div>
            {progress && <div className="text-emerald-300 text-xs font-mono">⏳ {progress}</div>}
            {err && <div className="text-red-400 text-xs font-mono break-all" data-testid="egw-error">{err}</div>}
            <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={onClose} className="cm-btn-ghost text-xs px-3 py-1.5">Cancel</button>
                <button type="submit" disabled={busy || !form.cluster_id || !form.host_network_id} className="cm-btn-primary text-xs px-3 py-1.5 disabled:opacity-50" data-testid="egw-submit">
                    {busy ? "Provisioning…" : "Deploy Egress GW"}
                </button>
            </div>
        </form>
    );
}

function IpPoolForm({ onClose, presetPurpose = "public" }) {
    const isEgress = presetPurpose === "egress";
    const [form, setForm] = useState(
        isEgress
            ? {
                name: "egress-uplink",
                subnet: "10.101.0.0/24",
                gateway: "10.101.0.1",
                range_first: "10.101.0.10",
                range_last: "10.101.0.250",
                dns_servers: "1.1.1.1,8.8.8.8",
                purpose: "egress",  // internal uplink pool for Egress-GW eth0
                bridge: "vmbr0",    // host bridge that routes to the Internet
            }
            : {
                name: "main",
                subnet: "185.87.21.128/25",
                gateway: "185.87.21.129",
                range_first: "185.87.21.220",
                range_last: "185.87.21.240",
                dns_servers: "1.1.1.1,8.8.8.8",
                purpose: "public",   // host LAN pool (LB / public-facing)
                bridge: "vmbr0",
            },
    );
    const [err, setErr] = useState(null);
    // Live-load bridges from the FIRST configured cluster so the operator
    // picks from real PVE interfaces instead of guessing a vmbr name.
    const { data: clusters } = useSWR("/api/admin/infra/proxmox", fetcher);
    const firstClusterId = clusters?.[0]?.id;
    const { data: bridgeList } = useSWR(
        firstClusterId
            ? `/api/admin/infra/proxmox/${firstClusterId}/bridges`
            : null,
        fetcher,
    );
    const bridges = bridgeList?.bridges || [];

    const submit = async (e) => {
        e.preventDefault(); setErr(null);
        try {
            await api.post("/api/admin/infra/host-networks", form);
            onClose();
        } catch (e) {
            setErr(e?.response?.data?.detail || String(e));
        }
    };
    return (
        <form onSubmit={submit} className="cm-card p-4 space-y-2" data-testid="ip-pool-form">
            <div className="cm-h3 mb-2">{isEgress ? "New Egress-Uplink Pool" : "New IP Pool"}</div>
            {isEgress && (
                <div className="text-xs text-zinc-400 bg-zinc-950 border border-purple-900 p-2 mb-1">
                    <strong className="text-purple-300">Egress-Uplink pool</strong> — an internal subnet (no public IP)
                    the <strong>Egress-GW eth0</strong> attaches to. The VM NATs service- &amp; tenant-zone traffic out
                    via this pool&apos;s gateway, which the Proxmox host then routes to the Internet. Pick the host
                    bridge (e.g. <code>vmbr0</code>) that has Internet reachability.
                </div>
            )}
            <Field label="Name" v={form.name} on={(v) => setForm({ ...form, name: v })} testid="ipp-name" />
            <FieldH label="Subnet CIDR" v={form.subnet} on={(v) => setForm({ ...form, subnet: v })} testid="ipp-subnet"
                     help="CIDR notation of the LAN. e.g. 185.87.21.128/25 — that covers .128 to .255." />
            <FieldH label="Default gateway" v={form.gateway} on={(v) => setForm({ ...form, gateway: v })} testid="ipp-gw"
                     help="The router IP that lives in this subnet. Find it on the Proxmox host via: ip route | grep default" />
            <FieldH label="First IP in pool" v={form.range_first} on={(v) => setForm({ ...form, range_first: v })} testid="ipp-first"
                     help="Lowest free IP you want CM to hand out to service VMs." />
            <FieldH label="Last IP in pool" v={form.range_last} on={(v) => setForm({ ...form, range_last: v })} testid="ipp-last"
                     help="Highest free IP you want CM to hand out. 20 IPs is plenty for the first deployment." />
            <FieldH label="DNS servers" v={form.dns_servers} on={(v) => setForm({ ...form, dns_servers: v })} testid="ipp-dns"
                     help="Comma-separated DNS resolvers the VMs use." />
            <label className="block">
                <span className="block text-xs text-zinc-400 uppercase tracking-wider mb-1 flex items-center gap-1">
                    Proxmox bridge (eth0 target)
                    <HelpIcon text="Which Proxmox bridge VMs in this pool connect to. vmbr0 = 99% of single-uplink setups. Use a different vmbr only on multi-WAN hosts (e.g. vmbr1 for a 2nd ISP)." />
                </span>
                {bridges.length > 0 ? (
                    <select
                        value={form.bridge || ""}
                        onChange={(e) => setForm({ ...form, bridge: e.target.value })}
                        className="cm-input w-full"
                        data-testid="ipp-bridge"
                    >
                        {bridges.map((b) => (
                            <option key={b.iface} value={b.iface}>
                                {b.iface}
                                {b.address ? `  ·  ${b.address}/${b.netmask || "?"}` : ""}
                                {b.active ? "" : "  (down)"}
                                {b.ports ? `  ·  ports: ${b.ports}` : ""}
                            </option>
                        ))}
                    </select>
                ) : (
                    <input
                        type="text"
                        value={form.bridge || ""}
                        onChange={(e) => setForm({ ...form, bridge: e.target.value })}
                        placeholder="vmbr0"
                        className="cm-input w-full font-mono"
                        data-testid="ipp-bridge"
                    />
                )}
                {bridgeList && bridges.length === 0 && (
                    <div className="text-[11px] text-amber-400 mt-1">
                        No bridges discovered on {bridgeList.node} — type the name manually.
                    </div>
                )}
            </label>
            {err && <div className="text-red-400 text-xs font-mono">{err}</div>}
            <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={onClose} className="cm-btn-ghost text-xs px-3 py-1.5">Cancel</button>
                <button type="submit" className="cm-btn-primary text-xs px-3 py-1.5" data-testid="ipp-submit">Save</button>
            </div>
        </form>
    );
}

function IpPoolPicker({ value, onChange }) {
    const { data } = useSWR("/api/admin/infra/host-networks", fetcher);
    return (
        <label className="block">
            <span className="block text-xs text-zinc-400 uppercase tracking-wider mb-1 flex items-center gap-1">
                IP Pool (for service VMs)
                <HelpIcon text="Pick an existing IP pool from the IP Pools tab. Service VMs will get IPs from this pool's range and use its gateway." />
            </span>
            <select
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                className="cm-input w-full"
                data-testid="px-svc-pool"
            >
                <option value="">— select pool —</option>
                {(data || []).map((p) => (
                    <option key={p.id} value={p.id}>
                        {p.name} — {p.subnet} ({p.range_first}–{p.range_last})
                    </option>
                ))}
            </select>
            {(!data || data.length === 0) && (
                <div className="text-[11px] text-amber-400 mt-1">
                    No IP pools defined yet. Create one in the <span className="font-mono">IP Pools</span> tab first.
                </div>
            )}
        </label>
    );
}

function IpPoolBadge({ id }) {
    const { data } = useSWR("/api/admin/infra/host-networks", fetcher);
    const pool = (data || []).find((p) => p.id === id);
    if (!pool) return <span className="text-zinc-500">{id?.slice(0, 8)}…</span>;
    return (
        <span className="text-zinc-200">
            {pool.name}{" "}
            <span className="text-zinc-500">({pool.subnet} · {pool.range_first}–{pool.range_last})</span>
        </span>
    );
}

// =========================================================
// Sprint 2 — Setup Guide: one-time NFS snippet operator must drop
// =========================================================
function SetupGuide({ onClose }) {
    const { data: snippetText } = useSWR("/api/public/snippet-template", (u) =>
        fetch(u).then((r) => r.text()),
    );
    const [copied, setCopied] = useState(false);

    const onCopy = async () => {
        if (!snippetText) return;
        try {
            await navigator.clipboard.writeText(snippetText);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (e) {
            alert("Copy failed — select the text manually");
        }
    };

    return (
        <div className="cm-card p-4 space-y-3" data-testid="setup-guide">
            <div className="flex items-start justify-between">
                <div>
                    <div className="cm-h3">Setup Guide — One-Time Operator Steps</div>
                    <div className="text-xs text-zinc-500 mt-1">
                        Do these once per Proxmox cluster. Never has to be touched again.
                    </div>
                </div>
                <button onClick={onClose} className="cm-btn-ghost text-xs px-2 py-1">×</button>
            </div>

            <Step n="1" title="Add a Proxmox cluster + assign node roles">
                Use <span className="text-[#CCFF00]">+ Add cluster</span>, then click
                <span className="text-[#CCFF00]"> Scan nodes</span> on the cluster card and
                mark each node as <span className="font-mono">control</span> or
                <span className="font-mono"> worker</span>.
            </Step>

            <Step n="2" title="Enable Snippets content-type on the shared storage">
                In Proxmox UI: <span className="font-mono">Datacenter → Storage → cloudmanager → Edit</span>
                → tick <span className="font-mono">Snippets</span> under Content.
                (Tenant disks need <span className="font-mono">Disk image</span>,
                Debian-13 download needs <span className="font-mono">Import</span>.)
            </Step>

            <Step n="3" title="Drop the static cm-bootstrap.yaml on NFS">
                Save the YAML below to <span className="font-mono">/mnt/internal/cloudman/snippets/cm-bootstrap.yaml</span>
                {" "}(your NFS export path). Once placed, never modify it.
                <div className="mt-2 relative">
                    <pre className="bg-zinc-950 border border-zinc-800 p-2 text-[11px] font-mono text-zinc-200 overflow-x-auto whitespace-pre" data-testid="setup-snippet">
                        {snippetText || "Loading…"}
                    </pre>
                    <button
                        onClick={onCopy}
                        data-testid="setup-snippet-copy"
                        className="absolute top-2 right-2 cm-btn-ghost text-[11px] px-2 py-0.5"
                    >
                        {copied ? "Copied!" : "Copy"}
                    </button>
                </div>
                <div className="text-[11px] text-zinc-500 italic mt-1">
                    The file holds nothing dynamic — VM-specific config is fetched on first
                    boot from <span className="font-mono">/api/public/service-vm-bootstrap/&lt;hostname&gt;.sh</span>.
                </div>
            </Step>

            <Step n="4" title="Setup networks + Debian-13 template">
                Back on the cluster card, click <span className="text-[#CCFF00]">Setup networks</span>{" "}
                (creates the cmtnt + cmsvc VXLAN zones) and
                <span className="text-[#CCFF00]"> Setup Debian-13</span>
                {" "}(downloads + converts the cloud image to a Proxmox template).
            </Step>

            <Step n="5" title="Provision your first service VM">
                Go to the <span className="text-[#CCFF00]">Redis Pools</span> /
                <span className="text-[#CCFF00]"> Collabora</span> tab, create a pool, then click
                <span className="text-[#CCFF00]"> Provision VM</span>. The VM clones from
                Debian-13, boots, fetches the cm-bootstrap snippet, then curls its own
                install script from NexTerminal. After 60-120s it appears as a healthy
                pool member.
            </Step>
        </div>
    );
}

function Step({ n, title, children }) {
    return (
        <div className="border-l-2 border-[#CCFF00] pl-3 py-1">
            <div className="text-xs uppercase tracking-widest font-mono text-[#CCFF00] mb-1">
                Step {n} — {title}
            </div>
            <div className="text-xs text-zinc-300 leading-relaxed">{children}</div>
        </div>
    );
}


// =========================================================
function NodesManager({ clusterId }) {
    const { data, mutate } = useSWR(
        `/api/admin/infra/proxmox/${clusterId}/nodes`,
        fetcher,
    );
    const [scanning, setScanning] = useState(false);
    const [settingUp, setSettingUp] = useState(false);
    const [buildingTpl, setBuildingTpl] = useState(false);
    const [scanResult, setScanResult] = useState(null);
    const [setupResult, setSetupResult] = useState(null);
    const [tplResult, setTplResult] = useState(null);
    const [err, setErr] = useState(null);

    const onScan = async () => {
        setScanning(true);
        setErr(null);
        try {
            const r = await api.post(`/api/admin/infra/proxmox/${clusterId}/nodes/scan`);
            setScanResult(r.data.summary);
            mutate();
        } catch (e) {
            setErr(e?.response?.data?.detail || String(e));
        } finally {
            setScanning(false);
        }
    };

    const onChangeRole = async (nodeName, role) => {
        setErr(null);
        try {
            await api.patch(
                `/api/admin/infra/proxmox/${clusterId}/nodes/${nodeName}`,
                { role },
            );
            mutate();
        } catch (e) {
            setErr(e?.response?.data?.detail || String(e));
        }
    };

    const onSetupNetworks = async () => {
        setSettingUp(true);
        setErr(null);
        try {
            const r = await api.post(
                `/api/admin/infra/proxmox/${clusterId}/setup-networks`,
                {},
            );
            setSetupResult(r.data);
        } catch (e) {
            setErr(e?.response?.data?.detail || String(e));
        } finally {
            setSettingUp(false);
        }
    };

    const onBuildDebianTemplate = async () => {
        setBuildingTpl(true);
        setErr(null);
        try {
            const r = await api.post(
                `/api/admin/infra/proxmox/${clusterId}/templates/ensure-debian13`,
                {},
            );
            setTplResult(r.data);
        } catch (e) {
            setErr(e?.response?.data?.detail || String(e));
        } finally {
            setBuildingTpl(false);
        }
    };

    const counts = (data || []).reduce(
        (acc, n) => ({ ...acc, [n.role]: (acc[n.role] || 0) + 1 }),
        {},
    );
    const canSetup =
        (counts.control || 0) >= 1 && (counts.worker || 0) >= 1;

    return (
        <div className="mt-4 border-t border-zinc-800 pt-3" data-testid={`nodes-manager-${clusterId}`}>
            <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-widest font-mono text-[#CCFF00] flex items-center gap-1">
                    <Server size={12} /> Cluster Nodes
                    <HelpIcon
                        testid={`nodes-help-${clusterId}`}
                        text="Discover Proxmox nodes and assign each one a role. Control nodes host CM-managed service VMs (Collabora/Redis/LB). Worker nodes host tenant Nextcloud VMs. Ignored nodes are excluded from any placement."
                    />
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={onScan}
                        disabled={scanning}
                        data-testid={`nodes-scan-${clusterId}`}
                        className="cm-btn-ghost text-xs px-2 py-1 flex items-center gap-1 disabled:opacity-50"
                    >
                        <RefreshCw size={12} className={scanning ? "animate-spin" : ""} />
                        {scanning ? "Scanning…" : "Scan nodes"}
                    </button>
                    <button
                        onClick={onSetupNetworks}
                        disabled={!canSetup || settingUp}
                        data-testid={`nodes-setup-networks-${clusterId}`}
                        className="cm-btn-primary text-xs px-2 py-1 flex items-center gap-1 disabled:opacity-30"
                        title={!canSetup ? "Need at least 1 control + 1 worker assigned" : "Create the tenant + service SDN zones"}
                    >
                        <Network size={12} />
                        {settingUp ? "Setting up…" : "Setup networks"}
                    </button>
                    <button
                        onClick={onBuildDebianTemplate}
                        disabled={!canSetup || buildingTpl}
                        data-testid={`nodes-build-debian-${clusterId}`}
                        className="cm-btn-ghost text-xs px-2 py-1 flex items-center gap-1 disabled:opacity-30"
                        title="Download Debian-13 cloud image and convert it to a Proxmox template (VMID 9013). Required before provisioning any service VM."
                    >
                        <HardDrive size={12} />
                        {buildingTpl ? "Building…" : "Setup Debian-13"}
                    </button>
                </div>
            </div>

            {(!data || data.length === 0) && (
                <div className="text-xs text-zinc-500 italic px-2 py-1">
                    No nodes scanned yet — click <span className="text-zinc-300">Scan nodes</span>.
                </div>
            )}

            {(data || []).length > 0 && (
                <div className="bg-zinc-950 border border-zinc-800 divide-y divide-zinc-800">
                    <div className="grid grid-cols-12 px-2 py-1 text-[11px] uppercase tracking-wider text-zinc-500 font-mono">
                        <div className="col-span-3">Node</div>
                        <div className="col-span-3">IP</div>
                        <div className="col-span-1">CPU</div>
                        <div className="col-span-2">RAM</div>
                        <div className="col-span-3">Role</div>
                    </div>
                    {data.map((n) => (
                        <div
                            key={n.id}
                            className="grid grid-cols-12 px-2 py-1.5 text-xs font-mono items-center"
                            data-testid={`node-row-${n.node_name}`}
                        >
                            <div className="col-span-3 text-zinc-200 flex items-center gap-1.5">
                                <span className={`inline-block w-1.5 h-1.5 rounded-full ${n.online ? "bg-emerald-400" : "bg-red-500"}`} />
                                {n.node_name}
                            </div>
                            <div className="col-span-3 text-zinc-400">{n.ip || "—"}</div>
                            <div className="col-span-1 text-zinc-400">{n.cpu_cores}</div>
                            <div className="col-span-2 text-zinc-400">{n.ram_mb ? `${(n.ram_mb / 1024).toFixed(0)}G` : "—"}</div>
                            <div className="col-span-3">
                                <select
                                    value={n.role || "unassigned"}
                                    onChange={(e) => onChangeRole(n.node_name, e.target.value)}
                                    data-testid={`node-role-${n.node_name}`}
                                    className="cm-input w-full text-xs py-0.5"
                                >
                                    <option value="unassigned">unassigned</option>
                                    <option value="control">control</option>
                                    <option value="worker">worker</option>
                                    <option value="ignored">ignored</option>
                                </select>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {scanResult && (
                <div className="mt-2 text-[11px] font-mono text-emerald-300">
                    Scan OK · {scanResult.new} new, {scanResult.updated} updated, {scanResult.total} total
                </div>
            )}
            {setupResult && (
                <div className={`mt-2 text-[11px] font-mono ${setupResult.ok ? "text-emerald-300" : "text-amber-300"}`}>
                    Networks {setupResult.ok ? "ready" : "partial"} · {(setupResult.zones || []).map((z) => `${z.zone}(${z.purpose})`).join(" + ")}
                    {(setupResult.push_errors || []).length > 0 && (
                        <div className="text-red-300 mt-1">
                            Push errors: {JSON.stringify(setupResult.push_errors)}
                        </div>
                    )}
                </div>
            )}
            {tplResult && (
                <div className="mt-2 text-[11px] font-mono text-emerald-300">
                    Debian-13 template {tplResult.existed ? "already on" : "created on"} {tplResult.node} (VMID {tplResult.vmid})
                </div>
            )}
            {err && <div className="mt-2 text-[11px] text-red-400 font-mono">{err}</div>}

            <SdnApplyAllCard clusterId={clusterId} />
        </div>
    );
}

// =========================================================
// SDN „Auf alle Nodes anwenden" — neuen Node (WK/CP) ins Netz aufnehmen
// =========================================================
function SdnApplyAllCard({ clusterId }) {
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [applying, setApplying] = useState(false);
    const [result, setResult] = useState(null);
    const [err, setErr] = useState(null);
    // Worker-IPs, die der Betreiber laut Preview bewusst ins EVPN-Peering nimmt.
    const [extraPeers, setExtraPeers] = useState({});

    const onPreview = async () => {
        setLoading(true); setErr(null); setResult(null);
        try {
            const r = await api.get(
                `/api/admin/infra/sdn/apply-all/preview?cluster_id=${clusterId}`);
            setPreview(r.data);
            setExtraPeers({});
        } catch (e) {
            setErr(e?.response?.data?.detail || String(e));
        } finally { setLoading(false); }
    };

    const onApply = async () => {
        const nNew = (preview?.nodes || []).filter((n) => n.is_new).length;
        const nPeers = (preview?.planned_control_peer_additions || []).length;
        const extra = Object.entries(extraPeers)
            .filter(([, v]) => v).map(([ip]) => ip);
        const msg = `Apply SDN cluster-wide (PUT /cluster/sdn)?\n\n`
            + `• ${nPeers} new control node(s) are added additively to peers/exitnodes\n`
            + `• ${extra.length} additional peer(s) (manually chosen)\n`
            + `• The apply renders the VNet bridges + FRR cluster-wide on ALL nodes\n\n`
            + `Existing peers/bridges remain unchanged (additive only). Continue?`;
        if (!window.confirm(msg)) return;
        setApplying(true); setErr(null);
        try {
            const r = await api.post(`/api/admin/infra/sdn/apply-all`, {
                cluster_id: clusterId, confirm: true, additional_peer_ips: extra,
            });
            setResult(r.data);
            await onPreview();
        } catch (e) {
            setErr(e?.response?.data?.detail || String(e));
        } finally { setApplying(false); }
    };

    return (
        <div className="mt-4 border-t border-zinc-800 pt-3" data-testid={`sdn-apply-all-${clusterId}`}>
            <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-widest font-mono text-[#CCFF00] flex items-center gap-1">
                    <Zap size={12} /> SDN auf alle Nodes anwenden
                    <HelpIcon
                        testid={`sdn-apply-help-${clusterId}`}
                        text="Onboards a newly added Proxmox node (control/worker) into the SDN: PUT /cluster/sdn renders the VNet bridges + FRR cluster-wide on ALL nodes. New control nodes are ADDED additively to controller.peers + zone.exitnodes (existing peers/bridges remain unchanged). Note: the Proxmox API does not report whether an SDN bridge is actually loaded on a SINGLE node — the reliable signal is the running SDN config (all VNets present). Preview (read-only) first, then confirmed apply."
                    />
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={onPreview}
                        disabled={loading || applying}
                        data-testid={`sdn-apply-preview-${clusterId}`}
                        className="cm-btn-ghost text-xs px-2 py-1 flex items-center gap-1 disabled:opacity-50"
                    >
                        <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
                        {loading ? "Checking…" : "Preview"}
                    </button>
                    <button
                        onClick={onApply}
                        disabled={!preview || applying || loading}
                        data-testid={`sdn-apply-run-${clusterId}`}
                        className="cm-btn-primary text-xs px-2 py-1 flex items-center gap-1 disabled:opacity-30"
                        title={!preview ? "Run preview first" : "Apply PUT /cluster/sdn cluster-wide"}
                    >
                        <Zap size={12} />
                        {applying ? "Wende an…" : "Anwenden"}
                    </button>
                </div>
            </div>

            {!preview && !err && (
                <div className="text-xs text-zinc-500 italic px-2 py-1">
                    Click <span className="text-zinc-300">Preview</span> to get the reliable
                    Ist-Zustand (Online-Nodes, EVPN-Peering, SDN-Config-Gesundheit) read-only aus Proxmox zu lesen.
                </div>
            )}

            {preview && (
                <div className="bg-zinc-950 border border-zinc-800 rounded p-2 space-y-2">
                    {/* SDN-Config-Gesundheit (zuverlässiges Cluster-Signal) */}
                    <div className={`text-xs font-mono flex items-center gap-1.5 ${preview.sdn_config_healthy ? "text-emerald-300" : "text-amber-300"}`}
                        data-testid={`sdn-apply-health-${clusterId}`}>
                        <span className={`inline-block w-1.5 h-1.5 rounded-full ${preview.sdn_config_healthy ? "bg-emerald-400" : "bg-amber-400"}`} />
                        {preview.sdn_config_healthy
                            ? `SDN-Config gesund — alle ${(preview.live_vnets || []).length} VNet(s) in der laufenden Config (Bridges cluster-weit gerendert).`
                            : `SDN config incomplete — ${(preview.vnets_missing_in_config || []).length} VNet(s) missing: ${(preview.vnets_missing_in_config || []).slice(0, 8).join(", ")}`}
                    </div>
                    <div className="text-xs text-zinc-400 font-mono" data-testid={`sdn-apply-summary-${clusterId}`}>
                        {preview.summary}
                    </div>
                    {(preview.warnings || []).map((w, i) => (
                        <div key={i} className="text-[11px] text-amber-300 font-mono flex items-start gap-1">
                            <Shield size={11} className="mt-0.5 shrink-0" /> {w}
                        </div>
                    ))}
                    {preview.drift && preview.drift.summary && preview.drift.summary !== "in_sync" && (
                        <div className="text-[11px] text-amber-300/90 font-mono">
                            Drift/Pending vs. CM-DB: {preview.drift.summary} — ein Apply committet den aktuellen Live-Zustand.
                        </div>
                    )}

                    {/* Per-Node: NUR zuverlässige Signale (Peering) — KEINE per-Node-Bridge-Behauptung */}
                    <div className="divide-y divide-zinc-800/70">
                        <div className="grid grid-cols-12 px-1 py-1 text-[11px] uppercase tracking-wider text-zinc-500 font-mono">
                            <div className="col-span-4">Node</div>
                            <div className="col-span-3">Rolle</div>
                            <div className="col-span-3">Underlay-IP</div>
                            <div className="col-span-2">EVPN-Peer</div>
                        </div>
                        {(preview.nodes || []).map((n) => (
                            <div key={n.node}
                                className="grid grid-cols-12 px-1 py-1 text-xs font-mono items-center"
                                data-testid={`sdn-node-${n.node}`}>
                                <div className="col-span-4 text-zinc-200 flex items-center gap-1.5">
                                    {n.node}
                                    {n.needs_peering && <span className="text-[10px] px-1 rounded bg-amber-400/20 text-amber-300">NEU · wird gepeert</span>}
                                    {(preview.vxlan_peer_gaps || []).some((g) => (g.missing_nodes || []).includes(n.node)) && (
                                        <span className="text-[10px] px-1 rounded bg-red-500/25 text-red-300"
                                            title="Node missing from at least one VXLAN zone peer list → overlay-isolated. Apply adds it.">
                                            VXLAN: fehlt
                                        </span>
                                    )}
                                </div>
                                <div className={`col-span-3 ${n.role === "unregistered" ? "text-amber-300" : "text-zinc-400"}`}>{n.role}</div>
                                <div className="col-span-3 text-zinc-400">{n.underlay_ip || "—"}</div>
                                <div className="col-span-2">
                                    {n.in_controller_peers
                                        ? <span className="text-emerald-400">✓ peered</span>
                                        : (n.role === "worker" && n.underlay_ip
                                            ? <label className="flex items-center gap-1 text-zinc-400 cursor-pointer" title="Worker manuell ins EVPN-Peering aufnehmen">
                                                <input type="checkbox"
                                                    data-testid={`sdn-extra-peer-${n.node}`}
                                                    checked={!!extraPeers[n.underlay_ip]}
                                                    onChange={(e) => setExtraPeers((s) => ({ ...s, [n.underlay_ip]: e.target.checked }))}
                                                /> +peer
                                              </label>
                                            : <span className="text-zinc-500">—</span>)}
                                </div>
                            </div>
                        ))}
                    </div>

                    {(preview.vxlan_peer_gaps || []).length > 0 && (
                        <div className="mt-2 rounded border border-red-500/50 bg-red-500/10 p-2"
                            data-testid={`sdn-vxlan-gaps-${clusterId}`}>
                            <div className="text-xs font-semibold text-red-300 flex items-center gap-1.5">
                                ⚠ VXLAN overlay gap — node(s) missing from the static peer list
                            </div>
                            <div className="text-[11px] text-zinc-300 mt-1 mb-1.5">
                                VMs migrated there would be <b>unreachable</b> in the overlay.
                                "Apply" adds the peers <b>additively</b> (nothing is removed).
                                Hinweis: das Regenerieren der VXLAN-Interfaces kann bestehende
                                Tenant-Verbindungen kurz flappen — idealerweise im Wartungsfenster.
                            </div>
                            {preview.vxlan_peer_gaps.map((g) => (
                                <div key={g.zone} className="text-[11px] font-mono text-zinc-300 py-0.5"
                                    data-testid={`sdn-vxlan-gap-${g.zone}`}>
                                    <span className="text-red-300">{g.zone}</span>
                                    {" — fehlt: "}
                                    <span className="text-amber-300">
                                        {(g.missing_nodes || []).map((n, i) => `${n} (${g.missing_ips[i]})`).join(", ")}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}

                    {(preview.planned_control_peer_additions || []).length > 0 && (
                        <div className="text-[11px] text-[#CCFF00] font-mono">
                            Beim Anwenden werden additiv gepeert: {preview.planned_control_peer_additions.map((p) => `${p.node} (${p.ip})`).join(", ")}
                        </div>
                    )}
                    <div className="text-[10px] text-zinc-500 font-mono">
                        Controller: {(preview.controllers || []).map((c) => `${c.name}[${(c.peers || []).length} peers]`).join(", ") || "keine (reines VXLAN)"}
                    </div>
                    {preview.note && (
                        <div className="text-[10px] text-zinc-500 font-mono border-t border-zinc-800/60 pt-1.5">
                            ℹ {preview.note}
                        </div>
                    )}
                </div>
            )}

            {result && (
                <div className={`mt-2 text-[11px] font-mono ${result.sdn_config_healthy ? "text-emerald-300" : "text-amber-300"}`}
                    data-testid={`sdn-apply-result-${clusterId}`}>
                    {result.sdn_config_healthy ? "✓ " : "⚠ "}{result.message}
                    {(result.changes || []).length > 0 && (
                        <div className="text-[#CCFF00] mt-1">
                            Changes: {result.changes.map((c) => c.controller
                                ? `${c.controller}: +${(c.added_peers || []).join(",")}`
                                : `${c.zone}: exit+${(c.added_exitnodes || []).join(",")}`).join(" · ")}
                        </div>
                    )}
                </div>
            )}
            {err && <div className="mt-2 text-[11px] text-red-400 font-mono" data-testid={`sdn-apply-err-${clusterId}`}>{err}</div>}
        </div>
    );
}

// =========================================================
// shared bits
// =========================================================
function Field({ label, v, on, testid, type = "text" }) {
    return (
        <div className="block">
            <label className="block text-xs text-zinc-400 uppercase tracking-wider mb-1">{label}</label>
            <input
                type={type}
                value={v ?? ""}
                onChange={(e) => on(e.target.value)}
                data-testid={testid}
                className="cm-input w-full"
            />
        </div>
    );
}

function RegisterExistingVmForm({ onClose }) {
    const [vmid, setVmid] = useState("");
    const [node, setNode] = useState("");
    const [kind, setKind] = useState("push-proxy");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [ok, setOk] = useState(null);

    const KINDS = [
        "push-proxy", "redis", "redis-lb", "collabora", "caddy", "egress-gw",
        "nextcloud", "nextcloud-web", "nextcloud-worker", "notify_push",
        "talk-signaling", "coturn", "appstore-proxy", "avscan", "avscan-lb",
        "archive-infra",
    ];

    const submit = async () => {
        if (!vmid || !node) { setErr("VMID and node required"); return; }
        setBusy(true); setErr(null); setOk(null);
        try {
            const r = await api.post("/api/admin/infra/service-vms/register-existing", {
                vmid: Number(vmid), node, kind,
            });
            setOk(r.data);
        } catch (e) {
            setErr(formatApiError(e));
        }
        setBusy(false);
    };

    return (
        <div className="cm-card p-4 border border-emerald-900/30">
            <div className="cm-h3 mb-3 flex items-center gap-2">
                <Server size={14} className="text-emerald-400" /> Register Existing VM
            </div>
            {ok ? (
                <div className="text-xs text-emerald-400 space-y-1 font-mono">
                    <div>VM registriert: {ok.id}</div>
                    <div>VMID: {ok.vmid} · IP: {ok.ip} · Kind: {ok.kind}</div>
                    <div className="flex gap-2 mt-2">
                        <button onClick={onClose} className="cm-btn-ghost text-xs">Close</button>
                    </div>
                </div>
            ) : (
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="text-[11px] text-zinc-500 uppercase mb-1 block">VMID</label>
                            <input value={vmid} onChange={(e) => setVmid(e.target.value)}
                                placeholder="627" type="number"
                                className="cm-input w-full text-xs font-mono" />
                        </div>
                        <div>
                            <label className="text-[11px] text-zinc-500 uppercase mb-1 block">Node</label>
                            <input value={node} onChange={(e) => setNode(e.target.value)}
                                placeholder="cp03"
                                className="cm-input w-full text-xs font-mono" />
                        </div>
                    </div>
                    <div>
                        <label className="text-[11px] text-zinc-500 uppercase mb-1 block">Kind</label>
                        <select value={kind} onChange={(e) => setKind(e.target.value)}
                            className="cm-input w-full text-xs font-mono">
                            {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
                        </select>
                    </div>
                    {err && <p className="text-xs text-red-400 font-mono">{err}</p>}
                    <div className="flex gap-2">
                        <button onClick={submit} disabled={busy || !vmid || !node}
                            className="cm-btn-primary text-xs px-3 py-1.5">
                            {busy ? "Registering…" : "Register VM"}
                        </button>
                        <button onClick={onClose} className="cm-btn-ghost text-xs px-3 py-1.5">Cancel</button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ---- Mail-Archiv Infrastructure Panel ------------------------------------
function ArchiveInfraPanel() {
    const { data, error, mutate } = useSWR(
        "/api/admin/infra/service-vms?kind=archive-infra", fetcher,
        { refreshInterval: 30000 });
    const vms = data?.service_vms || [];
    const onDelete = async (id) => {
        if (!confirm("Really delete the archive VM? All archive data and configurations of this tenant will be lost.")) return;
        try { await api.delete(`/api/admin/infra/service-vms/${id}`); mutate(); } catch (e) { alert(formatApiError(e)); }
    };
    return (
        <div className="space-y-4" data-testid="archive-infra-panel">
            <div className="text-xs text-zinc-400">
                <strong className="text-zinc-200">Mail-Archiv Infrastruktur</strong> — eine VM pro Tenant
                (Archive Agent + Elasticsearch + S3 Gateway). Der Archive Agent exponiert die REST-API
                for the <code>souvera_mailarchiv</code> Nextcloud app.
            </div>
            {error && <div className="text-red-400 text-xs font-mono break-all">{formatApiError(error)}</div>}
            {vms.length === 0 ? (
                <Empty title="No archive VMs"
                    hint="Archive-VMs werden automatisch provisioniert, wenn ein Tenant Mail-Archiv aktiviert." />
            ) : (
                <div className="cm-table-wrap">
                    <table className="cm-table">
                        <thead>
                            <tr>
                                <th className="cm-th">IP</th>
                                <th className="cm-th">Host</th>
                                <th className="cm-th">Status</th>
                                <th className="cm-th">Last Health</th>
                            </tr>
                        </thead>
                        <tbody>
                            {vms.map((v) => (
                                <tr key={v.id} className="cm-tr" data-testid={`archive-vm-${v.id}`}>
                                    <td className="cm-td font-mono text-xs">{v.ip_internal || "—"}</td>
                                    <td className="cm-td font-mono text-xs">{v.hostname || v.public_hostname || v.node || "—"}</td>
                                    <td className="cm-td">
                                        <StatusBadge status={v.status} />
                                    </td>
                                    <td className="cm-td text-[11px] font-mono text-zinc-500">
                                        {v.last_health_at ? new Date(v.last_health_at).toLocaleString("en-GB") : "—"}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
